/**
 * Comportamento da réplica da /ads-luna. Porte em JS puro do que na origem é React.
 *
 * Especificação: docs/tech/stack-test-sprint/ETAPA2_PORTE.md, no repo do MenoCore.
 * Não é biblioteca genérica — é o JS desta página. Extrair compartilhável só depois de
 * 2+ páginas reais, por decisão registrada do sprint.
 */
(function () {
  'use strict';

  /* ==========================================================================
     0. Config da página
     ==========================================================================
     Publicada pelo layout/aera-lp.liquid, num `case` por page.template_suffix. Seis
     valores separam uma página da outra: page_id do GA4, fallback de UTM e a identidade
     do modal de lead (os outros três — title, description e CSS extra — são resolvidos em
     Liquid e nunca chegam aqui).

     Os defaults abaixo são os da /ads-luna, de propósito: se o objeto sumir do HTML por
     qualquer motivo, o comportamento não muda para a página que já estava no ar. Um
     default que preserva o passado vale mais que um default "neutro" que quebraria em
     silêncio — mesmo raciocínio de ETAPA2_PORTE.md §6.1 sobre gate que falha fechado.
     ========================================================================== */
  var CFG = window.AERA_LP_CONFIG || {};
  CFG.page_id = CFG.page_id || 'ads_luna';
  CFG.utm_fallback = CFG.utm_fallback || { utm_source: 'ads_meta', utm_medium: 'lp_ads_luna' };
  CFG.lead = CFG.lead || {};
  CFG.lead.whatsapp_number = CFG.lead.whatsapp_number || '5548991947885';
  CFG.lead.product_name = CFG.lead.product_name || 'AERA40+ Luna';
  CFG.lead.form_name = CFG.lead.form_name || 'ads_luna_whatsapp_modal';
  // Checkout padrão da página. A /ads-luna tem seletor de ofertas e o initOffers sobrescreve
  // isto; a /loja-luna vende uma opção só e não tem seletor, então este é o valor final.
  CFG.checkout = CFG.checkout || '';

  /* ==========================================================================
     1. Entrada por scroll — `.vita-animate`
     ==========================================================================
     A armadilha nº 1 do porte. O CSS declara `.vita-animate { opacity: 0 }`, e o elemento
     só aparece quando esta classe `--visible` é adicionada. Sem este observer, 17
     elementos ficam invisíveis para sempre e seções inteiras somem da página.

     O observer REMOVE a classe ao sair do viewport, igual à origem
     (pages/ads-luna/index.js:150-161): o fade-out no scroll de volta é comportamento, não
     bug. Threshold 0.12, o mesmo.
     ========================================================================== */
  function initScrollReveal() {
    var els = document.querySelectorAll('.vita-animate');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      // Sem suporte, revela tudo: melhor a página inteira visível sem animação do que
      // seções invisíveis. É o modo de falha que importa.
      els.forEach(function (el) { el.classList.add('vita-animate--visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('vita-animate--visible');
        } else {
          entry.target.classList.remove('vita-animate--visible');
        }
      });
    }, { threshold: 0.12 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ==========================================================================
     2. Galeria do hero
     ==========================================================================
     Na origem o índice é UM estado só (`galleryIdx`) que move os dois tracks — mobile e
     desktop — ao mesmo tempo (pages/ads-luna/index.js:172-175). As duas galerias não são
     instâncias independentes: são a mesma galeria renderizada duas vezes, com o CSS
     escondendo a que não serve para a largura atual. Reproduzir isso importa: se o
     visitante girar o aparelho, a imagem não pula.

     O deslocamento é aplicado por JS (`style.transform`), não por CSS — não existe regra
     equivalente na folha.

     Swipe: limiar de 40px, nas duas galerias. Setas e dots em todas as instâncias.
     ========================================================================== */
  function initGalleries() {
    var galleries = Array.prototype.slice.call(document.querySelectorAll('[data-gallery]'));
    if (!galleries.length) return;

    var tracks = [];
    var dotGroups = [];
    var total = 0;

    galleries.forEach(function (gallery) {
      var track = gallery.querySelector('[data-gallery-track]');
      if (track) {
        tracks.push(track);
        total = Math.max(total, track.children.length);
      }
      var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-dot]'));
      if (dots.length) dotGroups.push(dots);
    });

    if (!total) return;
    var index = 0;

    /* ---- Autoplay (só a /loja-luna tem) -------------------------------------
       A /ads-luna NÃO tem autoplay: a galeria dela só anda por swipe, seta ou dot
       (pages/ads-luna/index.js). A /loja-luna avança sozinha a cada 3 s e PARA por 15 s a
       qualquer interação, retomando depois (pages/loja-luna/index.js:190-201, 205-215).

       O intervalo é UM só, no nível do initGalleries, não um por instância — as duas
       galerias compartilham o mesmo `index`, exatamente como o `galleryIdx` único da
       origem move os dois tracks. Dois intervalos brigariam pelo mesmo estado.

       Sem os atributos no HTML, `autoplayMs` é 0 e nada disso liga: a /ads-luna segue
       idêntica. */
    var primeira = galleries[0];
    var autoplayMs = parseInt(primeira.getAttribute('data-gallery-autoplay'), 10) || 0;
    /* `data-gallery-pause="0"` = NÃO retoma. É o comportamento da /loja-kit: lá o
       setAutoplayActive(false) do clique não tem contrapartida que devolva o true
       (pages/loja-kit/index.js:124 é a única atribuição), então interagir uma vez
       desliga o autoplay para sempre. A /loja-luna retoma em 15 s. Por isso `parseInt`
       com fallback só quando o atributo está AUSENTE — `|| 15000` engoliria o zero. */
    var pausaAttr = primeira.getAttribute('data-gallery-pause');
    var pausaMs = pausaAttr === null ? 15000 : (parseInt(pausaAttr, 10) || 0);
    var pausaNoHover = primeira.hasAttribute('data-gallery-hover-pause');
    var encerradoPorInteracao = false;
    var timerAutoplay = null;
    var timeoutPausa = null;

    function ligaAutoplay() {
      if (!autoplayMs || timerAutoplay !== null) return;
      timerAutoplay = window.setInterval(function () { goTo(index + 1); }, autoplayMs);
    }

    function pausaAutoplay() {
      if (!autoplayMs) return;
      if (timerAutoplay !== null) {
        window.clearInterval(timerAutoplay);
        timerAutoplay = null;
      }
      if (timeoutPausa !== null) window.clearTimeout(timeoutPausa);
      if (pausaMs <= 0) { encerradoPorInteracao = true; return; }  // /loja-kit
      timeoutPausa = window.setTimeout(function () {
        timeoutPausa = null;
        ligaAutoplay();
      }, pausaMs);
    }

    /* Pausa enquanto o ponteiro está sobre a galeria, e retoma ao sair — sem timeout.
       Só a /loja-kit tem (`isHovered`, pages/loja-kit/index.js:179). Distinta da pausa
       por interação: aqui o autoplay volta sozinho assim que o ponteiro sai, e a pausa
       por clique não volta nunca. As duas convivem: sair do hover só religa se a
       interação não tiver encerrado o autoplay antes. */
    function ligaHover(gallery) {
      if (!pausaNoHover) return;
      gallery.addEventListener('mouseenter', function () {
        if (timerAutoplay !== null) {
          window.clearInterval(timerAutoplay);
          timerAutoplay = null;
        }
      });
      gallery.addEventListener('mouseleave', function () {
        if (!encerradoPorInteracao) ligaAutoplay();
      });
    }

    /* Rótulo visível do slide atual — só a /loja-kit tem o elemento. O texto é lido do
       aria-label dos dots para não existirem duas listas de rótulo no DOM podendo divergir;
       nas outras páginas o aria-label é "Ver imagem N" e nada o exibe, porque o elemento
       não é emitido. A guarda é a EXISTÊNCIA do elemento, não o tamanho da lista. */
    var elsRotulo = Array.prototype.slice.call(document.querySelectorAll('[data-gallery-label]'));
    var rotulos = Array.prototype.slice.call((dotGroups[0] || []), 0)
      .map(function (dot) { return dot.getAttribute('aria-label') || ''; });

    function render() {
      tracks.forEach(function (track) {
        track.style.transform = 'translateX(-' + index * 100 + '%)';
      });
      elsRotulo.forEach(function (el) { el.textContent = rotulos[index] || ''; });
      dotGroups.forEach(function (dots) {
        dots.forEach(function (dot, i) {
          dot.classList.toggle('vita-gallery__dot--active', i === index);
        });
      });
    }

    function goTo(next) {
      index = ((next % total) + total) % total;
      render();
    }

    galleries.forEach(function (gallery) {
      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      ligaHover(gallery);
      if (prev) prev.addEventListener('click', function () { goTo(index - 1); pausaAutoplay(); });
      if (next) next.addEventListener('click', function () { goTo(index + 1); pausaAutoplay(); });

      gallery.querySelectorAll('[data-gallery-dot]').forEach(function (dot) {
        dot.addEventListener('click', function () {
          goTo(parseInt(dot.getAttribute('data-gallery-dot'), 10) || 0);
          pausaAutoplay();
        });
      });

      var startX = null;
      gallery.addEventListener('touchstart', function (event) {
        startX = event.touches[0].clientX;
      }, { passive: true });

      gallery.addEventListener('touchend', function (event) {
        if (startX === null) return;
        var diff = startX - event.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          pausaAutoplay();
          goTo(diff > 0 ? index + 1 : index - 1);
        }
        startX = null;
      }, { passive: true });
    });

    render();
    ligaAutoplay();
  }

  /* ==========================================================================
     3. Tracking — o gate e os pushes de dataLayer
     ==========================================================================
     Porte literal de shouldBlockMarketing (hooks/useSessionTracking.js:113-115), somado ao
     setting de tema. Decisão D5.

     A cópia literal SOZINHA não é gate: `shopify.aera40mais.com.br` PASSA na checagem de
     hostname, porque termina em aera40mais.com.br. Quem bloqueia no QA em produção é a
     outra metade — as UTMs de QA. O setting de tema é a trava que não depende de alguém
     lembrar delas.
     ========================================================================== */
  var QA_UTM_SOURCE = 'menocore_qa';
  var QA_UTM_MEDIUM = 'test';

  function isQaMode(search) {
    var params = new URLSearchParams(search || window.location.search);
    return params.get('utm_source') === QA_UTM_SOURCE && params.get('utm_medium') === QA_UTM_MEDIUM;
  }

  function shouldBlockMarketing() {
    if (!window.AERA_LP_MARKETING_ENABLED) return true;
    return !window.location.hostname.endsWith('aera40mais.com.br') || isQaMode();
  }

  /**
   * Injeta o contêiner GTM — o MESMO do braço Vercel, GTM-T4TDXRSL.
   *
   * Por que o mesmo e não um gtag próprio: os 12 gatilhos do contêiner são todos
   * CUSTOM_EVENT lendo {{_event}} do dataLayer, sem um único seletor de DOM, padrão de URL
   * ou gatilho de clique. Ele é agnóstico de plataforma por construção — é por isso que
   * funciona nas LPs da Vercel mesmo tendo sido montado para outro contexto. Usar o mesmo
   * contêiner mantém as 9 tags do GA4 e as 7 do Meta Pixel idênticas nos dois braços, e
   * reduz a assimetria de instrumentação em vez de somar mais uma.
   *
   * A injeção passa pelo gate: se shouldBlockMarketing() barrar, o contêiner NÃO carrega.
   * Isso é diferente de bloquear só os pushes — a tag `GA4 - Base` dispara em
   * Initialization – All Pages, então o contêiner carregado já manda page_view sozinho.
   */
  function initGtm() {
    if (shouldBlockMarketing()) return;
    var id = window.AERA_LP_GTM_ID;
    if (!id) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    var script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(id);
    document.head.appendChild(script);
  }

  /**
   * Microsoft Clarity: mapas de clique e de rolagem e gravação de visitas (pedido do Ygor, 24/09/2026).
   * Mesmo portão do GTM (shouldBlockMarketing), e só com `AERA_LP_CLARITY_ID` publicado pelo layout.
   * O Clarity mascara o que é digitado por padrão. `page_id` e `hero` viram filtros no painel do Clarity,
   * para separar as páginas e os anúncios.
   */
  function initClarity() {
    if (shouldBlockMarketing()) return;
    var id = window.AERA_LP_CLARITY_ID;
    if (!id || window.clarity) return;

    window.clarity = function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    var script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.clarity.ms/tag/' + encodeURIComponent(id);
    document.head.appendChild(script);

    window.clarity('set', 'page_id', CFG.page_id);
    var hero = new URLSearchParams(window.location.search).get('hero');
    if (hero) window.clarity('set', 'hero', hero);
  }

  /**
   * Espelho dos eventos no lp-track (25/09/2026, P25 do dossiê MenoCore
   * docs/implementations/2026-09-23-lp-ads-kit-alta-performance/, D31).
   *
   * Cada pushEvent também vira um `trackEvent('event', { eventType, meta })` do
   * aera-lp-track.js, quando ele existe — é assim que a sessão em `lp_sessions` ganha o
   * clique de compra, o FAQ aberto, o vídeo tocado. NÃO passa pelo portão de marketing
   * (shouldBlockMarketing): o lp-track é analytics próprio, e o servidor decide o que é teste
   * pela origem da página, então rascunho e `theme dev` gravam `traffic_type: TEST` em vez
   * de ficarem cegos.
   *
   * Tabela de tradução, porque os nomes do dataLayer e os do lp-track não são os mesmos:
   *   - 'click_to_checkout' → eventType 'checkout_button_clicked', meta { option, offer }.
   *     O servidor grava `cta_clicked` e `cta_offer` a partir de meta.offer; o `item` do
   *     GTM é a oferta — o inverso do que o hook faz ao empurrar o click_to_checkout ao GTM.
   *   - 'lp_page_view' e 'lp_view_content' → NÃO espelha: o `init` do lp-track já registra
   *     o page_view da sessão, e um segundo `page_view` inflaria a contagem. O `view_item`
   *     (25/09/2026) também não: é o mesmo instante do `lp_view_content`, só para o GA4.
   *   - os demais → mesmo nome e mesma meta, como no braço Vercel, onde o hook empurra
   *     `{ event: type, ...meta }` ao GTM a partir do mesmo trackEvent.
   * `nomeNoLpTrack` existe para o único caso em que o mesmo push do GTM tem outro nome no
   * lp-track: o `whatsapp_modal_open` pós-fetch do lead, que é `whatsapp_click` na origem
   * (components/WhatsAppLeadModal.js:73). Explícito, e não por adivinhação da meta.
   */
  var NAO_ESPELHAR_NO_LP_TRACK = { lp_page_view: true, lp_view_content: true, view_item: true };

  function espelhaNoLpTrack(name, extra, nomeNoLpTrack) {
    if (!window.aeraLpTrack) return;
    if (NAO_ESPELHAR_NO_LP_TRACK[name]) return;
    var eventType = nomeNoLpTrack || name;
    var meta = {};
    var item = '';
    if (name === 'click_to_checkout') {
      eventType = 'checkout_button_clicked';
      item = extra && extra.item ? extra.item : '';
      meta = { option: item, offer: item };
    } else if (extra) {
      Object.keys(extra).forEach(function (k) { meta[k] = extra[k]; });
    }
    var payload = { eventType: eventType };
    if (Object.keys(meta).length) payload.meta = meta;
    try {
      window.aeraLpTrack.trackEvent('event', payload);
    } catch (err) {
      // Tracking nunca quebra a página.
    }
  }

  /**
   * Empurra o evento ao dataLayer, com os params que distinguem os braços.
   * `experiment_arm` e `page_id` são obrigatórios e precisam estar registrados como custom
   * dimensions no admin do GA4 ANTES do tráfego — registro não é retroativo.
   *
   * O espelho no lp-track vem ANTES do portão: ele não depende de o marketing estar ligado.
   */
  function pushEvent(name, extra, nomeNoLpTrack) {
    espelhaNoLpTrack(name, extra, nomeNoLpTrack);
    if (shouldBlockMarketing()) return;
    window.dataLayer = window.dataLayer || [];
    var payload = {
      event: name,
      experiment_arm: 'shopify_yampi',
      page_id: CFG.page_id
    };
    if (extra) {
      Object.keys(extra).forEach(function (k) { payload[k] = extra[k]; });
    }
    window.dataLayer.push(payload);
  }

  /* ==========================================================================
     4. UTMs no link de checkout
     ==========================================================================
     Porte da parte de UTM do appendUTMs (hooks/useSessionTracking.js:461-486): as 5 chaves
     da URL, mais o utmFallback quando a URL não traz nenhuma.

     Até 25/09/2026 `lp_session_id` e `lp_user_id` NÃO iam — não havia lp-track neste braço,
     então não havia o que injetar (assimetria declarada em ETAPA2_PORTE.md §6.3). Desde
     então há: o aera-lp-track.js (P25 do dossiê MenoCore
     docs/implementations/2026-09-23-lp-ads-kit-alta-performance/, D31) abre a sessão, e os
     dois ids entram no checkout como `metadata[lp_session_id]` e `metadata[lp_user_id]` —
     ver METADATA_SOURCES abaixo. Como parâmetro de query (`?lp_session_id=`) eles NÃO vão:
     o link da Yampi não os devolve no pedido, e o metadata devolve.

     Depois disso o link passa por ymp_getUrlWithUtms, do YampiSnippet, que reanexa as UTMs
     do cookie e o _ga. É a ponte de atribuição deste braço (decisão D7).

     PORÉM o _ga que o snippet da Yampi anexa à URL NÃO sobrevive até o pedido: os cinco
     slots utm_* são os únicos campos de rastreio do recurso `order`. Medido em 27/08/2026
     no pedido 171166330 — o webhook chegou sem client_id, sem fbp e sem fbc.

     O canal que sobrevive é `metadata[chave]=valor` na URL do checkout: a Yampi grava no
     pedido e devolve em todo webhook `order.*` (help.yampi.com.br/pt-BR/articles/12166025).
     O app Shopify da Yampi já usa esse mesmo bucket para cart_id, platform e
     platform_order_number — o que se vê em `metadata.data` do payload.

     Daí METADATA_SOURCES: client_id do GA4 e os cookies do Meta viajam como metadata, e o
     purchase server-side consegue costurar a compra à sessão que a originou.

     Os cookies são lidos NO CLIQUE. Até 29/09/2026 `_fbp` e `_fbc` só existiam se o pixel
     (via GTM) tivesse carregado antes do clique; desde então garantirCookiesMeta(), logo
     abaixo e primeira linha do init(), grava os dois a partir do `fbclid` da URL quando o
     pixel ainda não gravou. E se o cookie `_fbc` falhar mesmo assim, `metadata[fbc]` cai no
     `fbclid` guardado no localStorage (fbcDoStorage), por até 90 dias. O `_ga` não tem fallback honesto: sem
     o cookie do GA não há client_id.
     ========================================================================== */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var UTM_FALLBACK = CFG.utm_fallback;

  function lerCookie(nome) {
    var partes = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < partes.length; i++) {
      var par = partes[i].trim();
      if (par.indexOf(nome + '=') === 0) return decodeURIComponent(par.slice(nome.length + 1));
    }
    return '';
  }

  /* ==========================================================================
     4a. Cookies da Meta a partir do `fbclid` (29/09/2026, D5)
     ==========================================================================
     Por quê: no pedido da Soeli (Yampi 174151671, 28/09 20:09, iPhone no navegador do
     Instagram) o Purchase da CAPI e o aviso do Telegram saíram sem `fbp` e sem `fbc`: os
     cookies não existiam no clique (pixel lento ou barrado no in-app), embora a URL de
     entrada tivesse `fbclid`. Diagnóstico no dossiê MenoCore
     docs/implementations/2026-09-29-analytics-trafego-desempenho/, registro
     historico/2026-09-29-1515-BRT-limpa-2109-sessoes-incompletas-fbc.md §4; escopo em
     historico/2026-09-29-1545-BRT-brief-t2-fbc-fbclid-tema.md.

     Formato da Meta (Parâmetros de informações do cliente da API de Conversões):
       _fbc = fb.<subdomainIndex>.<creationTime em ms>.<fbclid>
       _fbp = fb.<subdomainIndex>.<creationTime em ms>.<número aleatório>
     subdomainIndex = 1 porque o cookie vale para `.aera40mais.com.br` (domínio de um nível).
     O creationTime do `_fbc` é o instante em que o `fbclid` foi visto pela primeira vez, por
     isso ele fica guardado em `aera_fbclid_at` junto com o valor em `aera_fbclid`.

     `_fbp` nunca é sobrescrito: se o pixel chegou primeiro, o cookie é dele; se chega
     depois, o pixel reaproveita o `_fbp` que já encontra (não cria outro).

     `_fbc` segue a regra do próprio pixel: o clique mais recente é a atribuição válida.
     Se a URL traz um `fbclid` DIFERENTE do que está no `_fbc` (o último campo, depois do
     terceiro ponto; o `fbclid` é base64url, sem ponto), o `_fbc` é regravado com o
     instante de agora e o localStorage é atualizado. Mesmo `fbclid` do cookie: não toca.

     O `fbclid` guardado vale 90 dias, a vida do cookie. Vencido, fbcDoStorage() devolve ''
     (o checkout sai sem `metadata[fbc]`) e ele não serve de fonte para gravar `_fbc`; se
     o mesmo `fbclid` reaparecer na URL depois de vencido, conta como visto agora.

     Mesmo portão do GTM (shouldBlockMarketing): fora do domínio real ou em QA não grava
     nada. Nada aqui pode quebrar a página: tudo dentro de try/catch.

     O atributo `domain` só entra no domínio real. No `theme dev` (127.0.0.1) ele faria o
     navegador recusar o cookie, então lá o cookie vai sem `domain`, preso ao host.
     ========================================================================== */
  var DOMINIO_COOKIE_META = 'aera40mais.com.br';
  var VIDA_COOKIE_META_S = 7776000; // 90 dias, a vida que o pixel dá ao _fbp/_fbc

  function gravarCookie(nome, valor) {
    var host = window.location.hostname || '';
    var partes = [nome + '=' + encodeURIComponent(valor), 'path=/', 'max-age=' + VIDA_COOKIE_META_S, 'SameSite=Lax'];
    if (window.location.protocol === 'https:') partes.push('Secure');
    if (host === DOMINIO_COOKIE_META || host.endsWith('.' + DOMINIO_COOKIE_META)) {
      partes.push('domain=.' + DOMINIO_COOKIE_META);
    }
    document.cookie = partes.join('; ');
  }

  /* localStorage pode lançar (Safari privado, alguns in-app): leitura vazia e escrita muda. */
  function lerLocal(chave) {
    try {
      return window.localStorage.getItem(chave) || '';
    } catch (err) {
      return '';
    }
  }

  function gravarLocal(chave, valor) {
    try {
      window.localStorage.setItem(chave, valor);
    } catch (err) {
      // Sem storage o cookie ainda é gravado; só o fallback do checkout fica sem fonte.
    }
  }

  /* `fb.1.<aera_fbclid_at>.<aera_fbclid>` do localStorage, ou '' se não houver os dois. */
  function fbcDoStorage() {
    var guardado = fbclidGuardado();
    return guardado ? 'fb.1.' + guardado.at + '.' + guardado.fbclid : '';
  }

  /* O `fbclid` do localStorage e o instante em que foi visto, ou null se não houver ou se
     passou da vida do cookie (VIDA_COOKIE_META_S). */
  function fbclidGuardado() {
    var fbclid = lerLocal('aera_fbclid');
    var at = lerLocal('aera_fbclid_at');
    if (!fbclid || !/^\d+$/.test(at)) return null;
    if (Date.now() - Number(at) > VIDA_COOKIE_META_S * 1000) return null;
    return { fbclid: fbclid, at: at };
  }

  /* `fb.1.<instante>.<fbclid>` → `<fbclid>`: tudo depois do terceiro ponto. */
  function fbclidDoCookie(fbc) {
    var campos = (fbc || '').split('.');
    return campos.length >= 4 ? campos.slice(3).join('.') : '';
  }

  function garantirCookiesMeta() {
    try {
      if (shouldBlockMarketing()) return;

      var agora = String(Date.now());
      var fbclidUrl = new URLSearchParams(window.location.search).get('fbclid') || '';
      var guardado = fbclidGuardado();
      var fbcAtual = lerCookie('_fbc');

      if (fbclidUrl) {
        if (!guardado || guardado.fbclid !== fbclidUrl) {
          gravarLocal('aera_fbclid', fbclidUrl);
          gravarLocal('aera_fbclid_at', agora);
          guardado = { fbclid: fbclidUrl, at: agora };
        }
        if (!fbcAtual) {
          gravarCookie('_fbc', 'fb.1.' + guardado.at + '.' + fbclidUrl);
        } else if (fbclidDoCookie(fbcAtual) !== fbclidUrl) {
          // Clique novo: vale o mais recente, como no pixel. Instante de agora nos dois lugares.
          gravarLocal('aera_fbclid_at', agora);
          gravarCookie('_fbc', 'fb.1.' + agora + '.' + fbclidUrl);
        }
      } else if (!fbcAtual && guardado) {
        gravarCookie('_fbc', 'fb.1.' + guardado.at + '.' + guardado.fbclid);
      }

      if (!lerCookie('_fbp')) {
        var aleatorio = 1000000000 + Math.floor(Math.random() * 9000000000); // sempre 10 dígitos
        gravarCookie('_fbp', 'fb.1.' + agora + '.' + aleatorio);
      }
    } catch (err) {
      // Cookie da Meta é melhoria de atribuição; nunca pode custar a página.
    }
  }

  /* O cookie _ga é `GA1.1.<client_id>`, e o client_id são os dois últimos campos
     (`1234567890.1698765432`). Mandar o cookie inteiro faria o Measurement Protocol
     rejeitar o evento em silêncio. */
  function gaClientId() {
    var bruto = lerCookie('_ga');
    if (!bruto) return '';
    var campos = bruto.split('.');
    return campos.length >= 4 ? campos.slice(-2).join('.') : '';
  }

  /* O session_id do GA4 mora no cookie _ga_<measurement id sem o G->. O braço Woo manda
     esse parâmetro, e sem ele o purchase é aceito com 204 e mesmo assim não aparece nos
     relatórios padrão: é o session_id que costura a venda à sessão que teve o anúncio.

     Dois formatos, e o GA4 usa o segundo hoje:
       GS1.1.<session_id>.<n>.<...>                    — antigo, terceiro campo
       GS2.1.s<session_id>$o5$g1$t...$j60$l0$h...      — atual, prefixado por 's'

     Ler o terceiro campo no formato GS2 devolve `s1787834731$o5$g1$...` inteiro, e o
     Measurement Protocol aceita isso com validationMessages VAZIO — o evento entra com um
     session_id que não existe e a costura de sessão simplesmente não acontece, sem erro
     nenhum. Foi o que o pedido 171170725 mostrou em 27/08/2026. */
  function gaSessionId() {
    var bruto = lerCookie('_ga_82ND1W18X9');
    if (!bruto) return '';
    var gs2 = bruto.match(/(?:^|[.])s(\d+)\$/);
    if (gs2) return gs2[1];
    var campos = bruto.split('.');
    return campos.length >= 3 && /^\d+$/.test(campos[2]) ? campos[2] : '';
  }

  var METADATA_SOURCES = {
    ga_client_id: gaClientId,
    ga_session_id: gaSessionId,
    fbp: function () { return lerCookie('_fbp'); },
    /* `_fbp` só do cookie: garantirCookiesMeta() garante que ele exista. `_fbc` cai no
       `fbclid` guardado quando o cookie não está lá no clique (seção 4a). */
    fbc: function () { return lerCookie('_fbc') || fbcDoStorage(); },
    /* O único user-agent que chega ao webhook é o do Guzzle da própria Yampi. Sem este
       campo a CAPI perde qualidade de match: client_user_agent e client_ip_address são
       o par que o Meta usa quando não há cookie. O IP vem do pedido (resource.ip). */
    client_user_agent: function () { return navigator.userAgent || ''; },
    /* A sessão do lp-track (25/09/2026, P25/D31 do dossiê MenoCore
       docs/implementations/2026-09-23-lp-ads-kit-alta-performance/). O `metadata[...]` é o
       ÚNICO canal que chega ao pedido da Yampi — o `?lp_session_id=` que o appendUTMs da
       Vercel põe na URL não sobrevive ao checkout dela. O webhook-unified do MenoCore passa a
       ler `metadata.lp_session_id` e gravar no pedido, fechando o P0.6 do BACKLOG para este
       braço. storedIds() lê o localStorage sem abrir sessão nova, e devolve '' sem o
       rastreador ou para crawler (que nunca recebe decoração). */
    lp_session_id: function () { return window.aeraLpTrack ? window.aeraLpTrack.storedIds().lp_session_id : ''; },
    lp_user_id: function () { return window.aeraLpTrack ? window.aeraLpTrack.storedIds().lp_user_id : ''; }
  };

  function buildCheckoutUrl(rawUrl) {
    var url;
    try {
      url = new URL(rawUrl);
    } catch (err) {
      return rawUrl;
    }

    var params = new URLSearchParams(window.location.search);
    var hasUtms = false;
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        url.searchParams.set(key, value);
        hasUtms = true;
      }
    });

    if (!hasUtms) {
      Object.keys(UTM_FALLBACK).forEach(function (key) {
        url.searchParams.set(key, UTM_FALLBACK[key]);
      });
    }

    /* Antes do snippet da Yampi: ele mexe só em utm_* e _ga, e não toca no metadata. */
    Object.keys(METADATA_SOURCES).forEach(function (chave) {
      var valor = '';
      try {
        valor = METADATA_SOURCES[chave]() || '';
      } catch (err) {
        valor = '';
      }
      if (valor) url.searchParams.set('metadata[' + chave + ']', valor);
    });

    var final = url.toString();
    if (typeof window.ymp_getUrlWithUtms === 'function') {
      try {
        final = window.ymp_getUrlWithUtms(final) || final;
      } catch (err) {
        // O snippet da Yampi falhando não pode custar a venda.
      }
    }
    return final;
  }

  /* ==========================================================================
     5. Seletor de ofertas, previsão de entrega e CTA
     ========================================================================== */
  var state = { offer: 'single', checkout: CFG.checkout, flavor: '', flavorHash: '' };

  /**
   * Sabor do Luna (17/09/2026, D17). Cada <option> do seletor carrega o hash do link
   * Yampi daquele SKU — na Yampi não há produto pai; cada sabor é um produto com link
   * próprio. O link da oferta ativa segue com `quantity` e `promocode`; só o hash muda.
   */
  function comSabor(url, hash) {
    if (!url || !hash) return url;
    return url.replace(/\/r\/[A-Za-z0-9]+/, '/r/' + hash);
  }

  /**
   * Um sabor por pote (17/09/2026, D23 — revoga D22). A Yampi aceita vários SKUs no mesmo
   * link: `/r/HASH_A:2,HASH_B:1?promocode=...` — cada item é `token:quantidade`, separados
   * por vírgula (help.yampi.com.br/pt-BR/articles/6067074). Medido pelo Ygor em 17/09 com o
   * pedido 1521888335685296: dois SKUs, quantidades certas, cupom `aeraluna3x` aplicado.
   *
   * Quando todos os potes têm o mesmo sabor a URL fica no formato antigo (`/r/HASH?quantity=3`),
   * que é o que está em produção e provado. O formato `token:qtd` só entra quando há mistura,
   * e aí o `quantity=` da query sai — os dois juntos são ambíguos e a Yampi não documenta a
   * precedência.
   */
  function comSabores(url, hashes) {
    if (!url || !hashes || !hashes.length) return url;
    var contagem = {};
    var ordem = [];
    hashes.forEach(function (hash) {
      if (!hash) return;
      if (!contagem[hash]) { contagem[hash] = 0; ordem.push(hash); }
      contagem[hash] += 1;
    });
    if (ordem.length <= 1) return comSabor(url, ordem[0]);
    var tokens = ordem.map(function (hash) { return hash + ':' + contagem[hash]; }).join(',');
    var comTokens = url.replace(/\/r\/[A-Za-z0-9:,]+/, '/r/' + tokens);
    try {
      var parsed = new URL(comTokens);
      parsed.searchParams.delete('quantity');
      return parsed.toString();
    } catch (err) {
      return comTokens.replace(/([?&])quantity=\d+&?/, '$1').replace(/[?&]$/, '');
    }
  }

  /** Quantos potes a oferta ativa leva — lido do `quantity=` do link; 1 quando não há. */
  function potesDaOferta() {
    try {
      var qtd = parseInt(new URL(state.checkout).searchParams.get('quantity') || '1', 10);
      return isNaN(qtd) || qtd < 1 ? 1 : qtd;
    } catch (err) {
      return 1;
    }
  }

  function textOf(selector, value) {
    document.querySelectorAll(selector).forEach(function (el) {
      if (value === null || value === '') {
        el.textContent = '';
        el.hidden = true;
      } else {
        el.textContent = value;
        el.hidden = false;
      }
    });
  }

  function initOffers() {
    var buttons = Array.prototype.slice.call(document.querySelectorAll('[data-offer]'));
    if (!buttons.length) return;

    function select(button, emitEvent) {
      state.offer = button.getAttribute('data-offer');
      state.checkout = button.getAttribute('data-offer-checkout') || '';

      buttons.forEach(function (other) {
        var active = other === button;
        // Dois prefixos, mesmo seletor: a /ads-luna usa `luna-purchase-option*`; Vita e Kit
        // usam `lp-purchase-option*`. Até 14/09/2026 só o primeiro era alternado — os preços
        // trocavam, mas o radio da Vita e do Kit ficava parado na opção inicial.
        other.classList.toggle('luna-purchase-option--active', active);
        other.classList.toggle('lp-purchase-option--active', active);
        other.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      // Os quatro nós do card, mais os dois espelhos da sticky bar.
      textOf('[data-price-old]', 'de ' + button.getAttribute('data-offer-old'));
      textOf('[data-price-now]', button.getAttribute('data-offer-price'));
      textOf('[data-price-installment]', button.getAttribute('data-offer-installment'));
      textOf('[data-price-savings]', button.getAttribute('data-offer-savings'));
      textOf('[data-sticky-price]', button.getAttribute('data-offer-price'));
      textOf('[data-sticky-installment]', button.getAttribute('data-offer-installment'));

      var select = document.querySelector('[data-sticky-select]');
      if (select && select.value !== state.offer) select.value = state.offer;

      if (emitEvent) pushEvent('option_select', { option: state.offer });
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function () { select(button, true); });
    });

    // Sincroniza o <select> da sticky bar com os botões do card.
    var stickySelect = document.querySelector('[data-sticky-select]');
    if (stickySelect) {
      stickySelect.addEventListener('change', function () {
        var target = buttons.filter(function (b) { return b.getAttribute('data-offer') === stickySelect.value; })[0];
        if (target) select(target, true);
      });
    }

    var initial = buttons.filter(function (b) { return b.classList.contains('luna-purchase-option--active') || b.classList.contains('lp-purchase-option--active'); })[0] || buttons[0];
    select(initial, false);
  }

  /**
   * Previsão de entrega: hoje+5 a hoje+10, no formato dd/mm.
   * A origem renderiza '—' até montar, para não quebrar a hidratação do React
   * (pages/ads-luna/index.js:404). Aqui o '—' é servido e isto o substitui.
   */
  function initDeliveryRange() {
    var targets = document.querySelectorAll('[data-delivery-range]');
    if (!targets.length) return;

    function fmt(days) {
      var d = new Date();
      d.setDate(d.getDate() + days);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }

    var range = fmt(5) + ' – ' + fmt(10);
    targets.forEach(function (el) { el.textContent = range; });
  }

  /**
   * CTA de compra.
   *
   * O Promise.race de 300 ms é reprodução fiel de pages/ads-luna/index.js:196-201, e não é
   * detalhe: navegar na hora mata o click_to_checkout / InitiateCheckout no unload de parte
   * das sessões, e o braço Shopify apareceria com menos InitiateCheckout por artefato de
   * implementação. Esperar o evento inteiro atrasaria o CTA principal de forma perceptível.
   *
   * `position` é enviado no evento local mas NÃO chega ao dataLayer no braço Vercel — o
   * push de click_to_checkout leva só { event, event_id, item }
   * (hooks/useSessionTracking.js:195). Reproduzir sem ele mantém o schema do GA4 igual nos
   * dois braços.
   */
  function initBuyButtons() {
    document.querySelectorAll('[data-buy]').forEach(function (button) {
      button.addEventListener('click', function () {
        // Sabor obrigatório (D17): sem escolha, alerta inline, foco no seletor e nada de navegar.
        // O seletor do card pode ser do Luna (/ads-luna, /loja-luna) ou do Luna dentro do Kit
        // (/ads-kit, /loja-kit — MenoCore 2026-09-18-sabor-kit-rotas/D3); o do cross-sell fica fora do card.
        var seletorLuna = document.querySelector('[data-price-card] [data-flavor-selector]');
        if (seletorLuna && !state.flavor) {
          marcarErroSabor(seletorLuna, true);
          pushEvent('flavor_missing', { option: state.offer, position: button.getAttribute('data-buy') || '' });
          seletorLuna.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var sel = seletorLuna.querySelector('select');
          if (sel) sel.focus({ preventScroll: true });
          return;
        }
        // Compre 2, leve 3 com seletor de sabor: pergunta antes de sair — os três iguais ou um
        // sabor por pote (D23). Só abre quando o popup está na página e a oferta leva mais de um pote.
        var dialog = document.querySelector('[data-flavor-dialog]');
        if (seletorLuna && dialog && potesDaOferta() > 1) {
          abrirDialogoSabor(dialog, button.getAttribute('data-buy') || 'price_card');
          return;
        }
        irAoCheckout(comSabor(state.checkout, state.flavorHash));
      });
    });
  }

  /** Único ponto de saída da oferta principal. O Promise.race de 300 ms está explicado acima. */
  function irAoCheckout(urlBruta) {
    var destination = buildCheckoutUrl(urlBruta);
    Promise.race([
      new Promise(function (resolve) {
        pushEvent('click_to_checkout', { item: state.offer });
        resolve();
      }),
      new Promise(function (resolve) { setTimeout(resolve, 300); })
    ]).then(function () {
      window.location.href = destination;
    });
  }

  /**
   * Popup "um sabor por pote" (aera-flavor-dialog). Porte de LandingFlavorModeDialog.js.
   *
   * Os <select> do popup nascem só com "Não selecionado": as opções (id, rótulo e hash) são
   * copiadas do seletor do card na abertura, para o hash continuar tendo UMA fonte no tema.
   */
  function abrirDialogoSabor(dialog, position) {
    var seletorCard = document.querySelector('[data-price-card] [data-flavor-selector] select');
    var opcoes = seletorCard ? Array.prototype.slice.call(seletorCard.options).filter(function (o) { return o.value; }) : [];
    var potes = potesDaOferta();

    dialog.querySelectorAll('[data-dialog-flavor]').forEach(function (select, indice) {
      var linha = select.closest('.lp-flavor__row');
      if (linha) linha.hidden = indice >= potes;
      while (select.options.length > 1) select.remove(1);
      opcoes.forEach(function (o) {
        var nova = document.createElement('option');
        nova.value = o.value;
        nova.textContent = o.text;
        nova.setAttribute('data-hash', o.getAttribute('data-hash') || '');
        select.appendChild(nova);
      });
      // Começa com o sabor já escolhido no card em todos os potes — a cliente só troca o que quiser.
      select.value = state.flavor;
      select.classList.toggle('lp-flavor__select--empty', !select.value);
    });

    var botaoIguais = dialog.querySelector('[data-dialog-same]');
    var rotulo = seletorCard && seletorCard.selectedIndex >= 0 ? seletorCard.options[seletorCard.selectedIndex].text : '';
    if (botaoIguais) botaoIguais.textContent = rotulo ? 'Os três de ' + rotulo : 'Os três do mesmo sabor';

    dialog.setAttribute('data-dialog-position', position);
    mostrarPassoDialogo(dialog, 'choice');
    var erro = dialog.querySelector('[data-dialog-error]');
    if (erro) erro.hidden = true;
    dialog.hidden = false;
    pushEvent('flavor_dialog_open', { option: state.offer, position: position });
  }

  function mostrarPassoDialogo(dialog, passo) {
    dialog.setAttribute('data-dialog-step', passo);
    dialog.querySelectorAll('[data-dialog-panel]').forEach(function (painel) {
      painel.hidden = painel.getAttribute('data-dialog-panel') !== passo;
    });
  }

  function fecharDialogoSabor(dialog) {
    dialog.hidden = true;
  }

  function initFlavorDialog() {
    var dialog = document.querySelector('[data-flavor-dialog]');
    if (!dialog) return;

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) fecharDialogoSabor(dialog);
    });
    var fechar = dialog.querySelector('[data-dialog-close]');
    if (fechar) fechar.addEventListener('click', function () { fecharDialogoSabor(dialog); });

    var iguais = dialog.querySelector('[data-dialog-same]');
    if (iguais) {
      iguais.addEventListener('click', function () {
        pushEvent('flavor_mode_select', { mode: 'same' });
        fecharDialogoSabor(dialog);
        irAoCheckout(comSabor(state.checkout, state.flavorHash));
      });
    }

    var cadaUm = dialog.querySelector('[data-dialog-each]');
    if (cadaUm) {
      cadaUm.addEventListener('click', function () {
        pushEvent('flavor_mode_select', { mode: 'each' });
        mostrarPassoDialogo(dialog, 'each');
      });
    }

    dialog.querySelectorAll('[data-dialog-flavor]').forEach(function (select) {
      select.addEventListener('change', function () {
        select.classList.toggle('lp-flavor__select--empty', !select.value);
        var erro = dialog.querySelector('[data-dialog-error]');
        if (erro) erro.hidden = true;
        if (select.value) pushEvent('flavor_select', { flavor: select.value, pot: parseInt(select.getAttribute('data-dialog-flavor'), 10) || 1 });
      });
    });

    var iniciar = dialog.querySelector('[data-dialog-start]');
    if (iniciar) {
      iniciar.addEventListener('click', function () {
        var selects = Array.prototype.slice.call(dialog.querySelectorAll('[data-dialog-flavor]')).filter(function (s) {
          var linha = s.closest('.lp-flavor__row');
          return !linha || !linha.hidden;
        });
        var incompleto = selects.some(function (s) { return !s.value; });
        var erro = dialog.querySelector('[data-dialog-error]');
        if (incompleto) {
          if (erro) erro.hidden = false;
          var primeiro = selects.filter(function (s) { return !s.value; })[0];
          if (primeiro) primeiro.focus();
          return;
        }
        var hashes = selects.map(function (s) { return s.options[s.selectedIndex].getAttribute('data-hash') || ''; });
        fecharDialogoSabor(dialog);
        irAoCheckout(comSabores(state.checkout, hashes));
      });
    }
  }

  function marcarErroSabor(seletor, erro) {
    seletor.classList.toggle('lp-flavor--error', erro);
    var msg = seletor.querySelector('[data-flavor-error]');
    if (msg) msg.hidden = !erro;
    var sel = seletor.querySelector('select');
    if (sel) sel.setAttribute('aria-invalid', erro ? 'true' : 'false');
  }

  /**
   * Seletores de sabor. O do card de preço alimenta `state.flavor/flavorHash` (e o resumo
   * da sticky bar), seja do Luna ou do Luna dentro do Kit (/ads-kit e /loja-kit desde 20/09,
   * MenoCore 2026-09-18-sabor-kit-rotas/D3); o do cross-sell do kit fica fora do card e é lido
   * na hora do clique em [data-kit-buy]. `pot` distingue no evento, como no braço Vercel
   * (1 = pote do card, 'kit' = Luna do kit).
   */
  function initFlavorSelectors() {
    document.querySelectorAll('[data-flavor-selector]').forEach(function (seletor) {
      var select = seletor.querySelector('[data-flavor-select]');
      if (!select) return;
      var produto = seletor.getAttribute('data-flavor-product') || 'luna';
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        var id = select.value;
        select.classList.toggle('lp-flavor__select--empty', !id);
        marcarErroSabor(seletor, false);
        if (seletor.closest('[data-price-card]')) {
          state.flavor = id;
          state.flavorHash = option ? (option.getAttribute('data-hash') || '') : '';
          // Linha própria no rodapé da sticky bar (17/09): "Sabor: Frutas Vermelhas".
          textOf('[data-sticky-flavor]', id ? 'Sabor: ' + option.text : '');
        }
        if (id) pushEvent('flavor_select', { flavor: id, pot: produto === 'kit' ? 'kit' : 1 });
      });
    });
  }

  /**
   * Cross-sell do Kit (D8). Mesmo evento do checkout principal, distinguido por
   * `item: 'kit_cross_sell'`; o sabor do Luna do kit vem do seletor da própria seção.
   */
  function initKitCrossSell() {
    document.querySelectorAll('[data-kit-buy]').forEach(function (button) {
      button.addEventListener('click', function () {
        var secao = button.closest('.lp-kit') || document;
        var seletor = secao.querySelector('[data-flavor-selector][data-flavor-product="kit"]');
        var select = seletor ? seletor.querySelector('select') : null;
        var option = select ? select.options[select.selectedIndex] : null;
        if (seletor && (!select || !select.value)) {
          marcarErroSabor(seletor, true);
          pushEvent('flavor_missing', { option: 'kit_cross_sell', position: 'cross_sell_section' });
          if (select) select.focus();
          return;
        }
        var hash = option ? option.getAttribute('data-hash') : '';
        var destination = buildCheckoutUrl(comSabor(button.getAttribute('data-kit-checkout') || '', hash));
        Promise.race([
          new Promise(function (resolve) {
            pushEvent('click_to_checkout', { item: 'kit_cross_sell' });
            resolve();
          }),
          new Promise(function (resolve) { setTimeout(resolve, 300); })
        ]).then(function () {
          window.location.href = destination;
        });
      });
    });
  }

  /**
   * Compra avulsa do VITA ou do LUNA na cópia ads-kit-2026-09-23 (etapa 11 do dossiê MenoCore
   * 2026-09-23-lp-ads-kit-alta-performance). Só age em `[data-single-buy]`, atributo que nenhuma
   * outra página tem. O valor do atributo é o `item` do click_to_checkout ('vita_single',
   * 'luna_single'). Com seletor de sabor no cartão (`[data-single-card]`), o sabor é obrigatório,
   * como no card de preço (D17), e troca o hash do link.
   */
  function initSingleBuy() {
    document.querySelectorAll('[data-single-buy]').forEach(function (button) {
      button.addEventListener('click', function () {
        var cartao = button.closest('[data-single-card]') || button.parentNode;
        var seletor = cartao.querySelector('[data-flavor-selector]');
        var select = seletor ? seletor.querySelector('select') : null;
        var item = button.getAttribute('data-single-buy') || 'single';
        if (seletor && (!select || !select.value)) {
          marcarErroSabor(seletor, true);
          pushEvent('flavor_missing', { option: item, position: 'single_section' });
          seletor.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (select) select.focus({ preventScroll: true });
          return;
        }
        var option = select ? select.options[select.selectedIndex] : null;
        var hash = option ? option.getAttribute('data-hash') : '';
        var destination = buildCheckoutUrl(comSabor(button.getAttribute('data-single-checkout') || '', hash));
        Promise.race([
          new Promise(function (resolve) {
            pushEvent('click_to_checkout', { item: item });
            resolve();
          }),
          new Promise(function (resolve) { setTimeout(resolve, 300); })
        ]).then(function () {
          window.location.href = destination;
        });
      });
    });
  }

  /** Botão da seção de garantia: rola até o card de preço (D18). */
  function initGuaranteeCta() {
    document.querySelectorAll('[data-scroll-to-offer]').forEach(function (button) {
      button.addEventListener('click', function () {
        pushEvent('guarantee_cta_click', { position: 'guarantee_section' });
        var card = document.querySelector('[data-price-card]');
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  /* ==========================================================================
     Contador diário "restam N unidades" — porte de lib/lpDailyScarcity.js (D9).
     ==========================================================================
     Determinístico por dia: mesma hora do mesmo dia mostra o mesmo número em qualquer
     dispositivo, e muda de um dia para o outro pela semente (data + rota). start ∈ [22,34]
     de manhã, end ∈ [2,6] à noite, janela 06:00–23:00 em America/Sao_Paulo, decaimento
     p' = 1 − (1 − p)^1.6. Nunca sobe dentro do dia. Sem literal de número na página.
     Só renderiza depois de calcular (o HTML servido esconde o sufixo), como no Vercel.
     ========================================================================== */
  function hashString(input) {
    var hash = 0x811c9dc5;
    for (var i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
  }

  function remainingUnits(now, seedKey) {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(now);
    function get(type) { var p = parts.filter(function (x) { return x.type === type; })[0]; return p ? p.value : ''; }
    var ymd = get('year') + '-' + get('month') + '-' + get('day');
    var minutes = Number(get('hour')) * 60 + Number(get('minute'));
    var seed = hashString(ymd + '|' + (seedKey || ''));
    var start = 22 + (seed % 13);
    var end = 2 + ((seed >>> 8) % 5);
    var raw = (minutes - 6 * 60) / (23 * 60 - 6 * 60);
    var progress = Math.min(1, Math.max(0, raw));
    var eased = 1 - Math.pow(1 - progress, 1.6);
    return Math.round(start - (start - end) * eased);
  }

  function initScarcity() {
    var items = document.querySelectorAll('[data-scarcity-seed]');
    if (!items.length) return;
    function tick() {
      items.forEach(function (item) {
        var units = remainingUnits(new Date(), item.getAttribute('data-scarcity-seed'));
        var n = item.querySelector('[data-scarcity-units]');
        var w = item.querySelector('[data-scarcity-word]');
        var sufixo = item.querySelector('[data-scarcity-suffix]');
        if (n) n.textContent = String(units);
        if (w) w.textContent = units === 1 ? 'unidade' : 'unidades';
        if (sufixo) sufixo.hidden = false;
      });
    }
    tick();
    window.setInterval(tick, 60000);
  }

  /* ==========================================================================
     6. Acordeão do FAQ
     ==========================================================================
     Um item aberto por vez, e nenhum nasce aberto — na origem `faqOpen` começa null
     (pages/ads-luna/index.js:135). Clicar no item já aberto fecha.
     ========================================================================== */
  function initFaq() {
    var toggles = Array.prototype.slice.call(document.querySelectorAll('[data-faq-toggle]'));
    if (!toggles.length) return;

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var item = toggle.closest('[data-faq-item]');
        if (!item) return;

        var jaAberto = item.classList.contains('vita-faq-item--open');
        document.querySelectorAll('[data-faq-item]').forEach(function (outro) {
          outro.classList.remove('vita-faq-item--open');
        });
        if (!jaAberto) item.classList.add('vita-faq-item--open');

        pushEvent('faq_toggle', { question: toggle.getAttribute('data-faq-question') || '' });
      });
    });
  }

  /* ==========================================================================
     7. Barra de compra fixa
     ==========================================================================
     A condição inteira importa: `!isIntersecting && boundingClientRect.top < 0`
     (pages/ads-luna/index.js:167). A barra só aparece quando o card de preço já saiu por
     CIMA da tela. Sem a segunda metade ela nasceria visível no topo da página, cobrindo o
     hero e competindo com o próprio card que ela deveria substituir.
     ========================================================================== */
  function initStickyBuy() {
    var bar = document.querySelector('[data-sticky-buy]');
    var card = document.querySelector('[data-price-card]');
    if (!bar || !card || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var mostrar = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('vita-sticky-buy--show', mostrar);
    }, { threshold: 0.1 });

    io.observe(card);
  }

  /* ==========================================================================
     8. Modais
     ==========================================================================
     Os modais da página NÃO são uniformes, e padronizá-los mudaria o comportamento de
     três deles (ETAPA2_PORTE.md §5.10). Reproduzido como está:

     - ANVISA: tem Escape (registrado em `window`, não `document`) e scroll-lock.
     - Imagem do FAQ: não tem Escape nem scroll-lock.
     - Ambos fecham por clique no overlay; o conteúdo para a propagação.

     O cleanup do scroll-lock do ANVISA é incondicional na origem (AnvisaModal.js:15) —
     ele zera `body.style.overflow` em vez de restaurar o valor anterior. Mantido.
     ========================================================================== */
  function ligaOverlay(overlay, opcoes) {
    if (!overlay) return null;
    var config = opcoes || {};

    // O [hidden] sozinho não basta: `.anvisa-modal-overlay` e `.faq-modal-overlay`
    // declaram `display: flex`, e regra de autor vence a folha do navegador. O CSS do
    // porte tem a regra com !important; o style inline aqui é a segunda trava, para o
    // modal não aparecer aberto se o CSS demorar ou falhar.
    function abrir() {
      overlay.hidden = false;
      overlay.style.display = '';
      if (config.scrollLock) document.body.style.overflow = 'hidden';
      if (config.onOpen) config.onOpen();
    }

    function fechar() {
      overlay.hidden = true;
      overlay.style.display = 'none';
      if (config.scrollLock) document.body.style.overflow = '';
    }

    // Estado inicial explícito: fechado.
    overlay.hidden = true;
    overlay.style.display = 'none';

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) fechar();
    });

    var conteudo = overlay.querySelector('[data-modal-content]');
    if (conteudo) {
      conteudo.addEventListener('click', function (event) { event.stopPropagation(); });
    }

    if (config.escape) {
      window.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !overlay.hidden) fechar();
      });
    }

    return { abrir: abrir, fechar: fechar };
  }

  function initModals() {
    // Modal de imagem — sem Escape, sem scroll-lock (fiel à origem)
    var imagem = ligaOverlay(document.querySelector('[data-image-overlay]'), {
      escape: false,
      scrollLock: false
    });
    if (imagem) {
      var alvo = document.querySelector('[data-image-target]');
      document.querySelectorAll('[data-image-modal]').forEach(function (botao) {
        botao.addEventListener('click', function () {
          if (alvo) {
            alvo.src = botao.getAttribute('data-image-src') || '';
            alvo.alt = botao.getAttribute('data-image-alt') || '';
          }
          imagem.abrir();
        });
      });
      var fecharImagem = document.querySelector('[data-image-close]');
      if (fecharImagem) fecharImagem.addEventListener('click', imagem.fechar);
    }

    // Modal ANVISA — com Escape e scroll-lock
    var anvisa = ligaOverlay(document.querySelector('[data-anvisa-modal]'), {
      escape: true,
      scrollLock: true
    });
    if (anvisa) {
      document.querySelectorAll('[data-anvisa-open]').forEach(function (botao) {
        botao.addEventListener('click', function () {
          anvisa.abrir();
          pushEvent('anvisa_modal_open', { position: 'footer' });
        });
      });
      // São DOIS: o ✕ do topo e o botão "Entendido" do rodapé.
      document.querySelectorAll('[data-anvisa-close]').forEach(function (botao) {
        botao.addEventListener('click', anvisa.fechar);
      });
    }
  }

  /* ==========================================================================
     9. Links rastreados do rodapé e gatilho do modal de WhatsApp
     ==========================================================================
     O braço Vercel emite internal_link_click, external_link_click e anvisa_modal_open pelo
     Footer (components/Footer.js:28,91,106). Sem eles o braço Shopify teria superfície de
     evento menor, e a comparação de funil ficaria incompleta.
     ========================================================================== */
  function initFooterLinks() {
    document.querySelectorAll('[data-internal-link]').forEach(function (link) {
      link.addEventListener('click', function () {
        pushEvent('internal_link_click', {
          position: link.getAttribute('data-link-position') || '',
          destination: link.getAttribute('data-link-destination') || ''
        });
      });
    });

    document.querySelectorAll('[data-external-link]').forEach(function (link) {
      link.addEventListener('click', function () {
        pushEvent('external_link_click', {
          position: link.getAttribute('data-link-position') || '',
          destination: link.getAttribute('data-link-destination') || ''
        });
      });
    });
  }

  /* ==========================================================================
     10. Modal de vídeo, com player HLS e encadeamento
     ==========================================================================
     Safari toca HLS nativo; os demais precisam do hls.js, carregado SOB DEMANDA — só
     quando um vídeo abre. São 542 KB que a maioria das sessões nunca baixa
     (hooks/useHlsVideo.js:47-51).

     Ao terminar, o modal avança para o PRÓXIMO vídeo e só fecha no último
     (pages/ads-luna/index.js:758-769). Cada troca emite video_autoplay_next.
     ========================================================================== */
  var VIDEOS_EM_ORDEM = [];
  var hlsInstance = null;
  var hlsPromise = null;

  function carregaHls() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsPromise) return hlsPromise;

    hlsPromise = new Promise(function (resolve, reject) {
      var url = window.AERA_LP_HLS_URL;
      if (!url) { reject(new Error('URL do hls.js ausente')); return; }
      var script = document.createElement('script');
      script.src = url;
      script.onload = function () { resolve(window.Hls); };
      script.onerror = function () { reject(new Error('falha ao carregar hls.js')); };
      document.head.appendChild(script);
    });
    return hlsPromise;
  }

  function destroiHls() {
    if (hlsInstance) {
      try { hlsInstance.destroy(); } catch (err) { /* já destruído */ }
      hlsInstance = null;
    }
  }

  function abrirModalVideo(video) {
    var overlay = document.querySelector('[data-video-modal]');
    var player = document.querySelector('[data-video-player]');
    if (!overlay || !player || !video || !video.hls) return;

    destroiHls();
    player.removeAttribute('src');
    player.poster = video.poster || '';
    player.setAttribute('data-video-current', video.id || '');

    overlay.hidden = false;
    overlay.style.display = '';

    function toca() { player.play().catch(function () { /* autoplay bloqueado */ }); }

    if (player.canPlayType('application/vnd.apple.mpegurl')) {
      player.src = video.hls;
      toca();
    } else {
      carregaHls().then(function (Hls) {
        if (!Hls || !Hls.isSupported()) return;
        hlsInstance = new Hls({
          enableWorker: true,
          capLevelToPlayerSize: true,
          maxBufferLength: 20,
          backBufferLength: 0
        });
        hlsInstance.loadSource(video.hls);
        hlsInstance.attachMedia(player);
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, toca);
        hlsInstance.on(Hls.Events.ERROR, function (_e, data) {
          if (data && data.fatal) destroiHls();
        });
      }).catch(function () { /* sem player, o modal fica com o poster */ });
    }

    pushEvent('video_play', { video_id: video.id, position: 'hero_bubbles' });
  }

  function fecharModalVideo() {
    var overlay = document.querySelector('[data-video-modal]');
    var player = document.querySelector('[data-video-player]');
    destroiHls();
    if (player) { player.pause(); player.removeAttribute('src'); player.load(); }
    if (overlay) { overlay.hidden = true; overlay.style.display = 'none'; }
  }

  function initVideoModal() {
    var overlay = document.querySelector('[data-video-modal]');
    var player = document.querySelector('[data-video-player]');
    if (!overlay || !player) return;

    overlay.hidden = true;
    overlay.style.display = 'none';

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) fecharModalVideo();
    });
    var conteudo = overlay.querySelector('[data-modal-content]');
    if (conteudo) conteudo.addEventListener('click', function (e) { e.stopPropagation(); });
    var botaoFechar = overlay.querySelector('[data-video-close]');
    if (botaoFechar) botaoFechar.addEventListener('click', fecharModalVideo);

    // Encadeamento: avança para o próximo, fecha só no último.
    player.addEventListener('ended', function () {
      var atual = player.getAttribute('data-video-current');
      var i = -1;
      VIDEOS_EM_ORDEM.forEach(function (v, idx) { if (v.id === atual) i = idx; });

      if (i >= 0 && i < VIDEOS_EM_ORDEM.length - 1) {
        var proximo = VIDEOS_EM_ORDEM[i + 1];
        pushEvent('video_autoplay_next', { from: atual, to: proximo.id });
        abrirModalVideo(proximo);
      } else {
        fecharModalVideo();
      }
    });
  }

  /* ==========================================================================
     11. Carrossel de vídeos — o motor que controla 7,75 MB
     ==========================================================================
     Este é o trecho mais consequente do porte para performance, e o mais fácil de
     implementar errado.

     Os 7 posters somam ~191 KB. Os 7 GIFs animados de preview somam 7,75 MB — 3,7× todas
     as imagens locais da página juntas. O card nasce com o poster e só troca para o GIF
     quando fica visível, e o critério de "visível" é DIFERENTE por largura
     (components/StreamVideoCarousel.js:52-116):

     - Mobile (≤768px): APENAS o card mais à esquerda dentro da viewport do carrossel.
       Um GIF por vez.
     - Desktop: IntersectionObserver com root no próprio carrossel e threshold 0.5 —
       todos os cards com metade visível animam ao mesmo tempo.

     Implementar "anima todo mundo" custaria ~15 MB por sessão no mobile, e o braço Shopify
     perderia a comparação de performance por defeito de implementação, não por stack.

     O array é duplicado no DOM (14 cards para 7 vídeos), e o autoplay usa isso: ao chegar
     no índice 7 ele deixa a rolagem suave terminar e, 700 ms depois, reancora no índice 0
     com behavior 'auto' — o salto fica invisível porque o card 7 é idêntico ao card 0.
     ========================================================================== */
  var AUTOPLAY_INTERVAL_MS = 5000;
  var INTERACTION_PAUSE_MS = 15000;
  var LOOP_RESET_DELAY_MS = 700;

  function initVideoCarousel() {
    var carrossel = document.querySelector('[data-video-carousel]');
    if (!carrossel) return;

    var cards = Array.prototype.slice.call(carrossel.children);
    if (cards.length < 2) return;

    var totalVideos = cards.length / 2;

    // Ordem canônica dos 7 vídeos, lida da PRIMEIRA cópia — o encadeamento do modal
    // precisa dela para saber qual é o próximo.
    VIDEOS_EM_ORDEM = cards.slice(0, totalVideos).map(function (card) {
      return {
        id: card.getAttribute('data-video-id'),
        name: card.getAttribute('data-video-name'),
        hls: card.getAttribute('data-video-hls'),
        poster: card.getAttribute('data-video-poster')
      };
    });
    var media = window.matchMedia('(max-width: 768px)');
    var indiceAutoplay = 0;
    var pausado = false;
    var frame = null;
    var observer = null;
    var timeoutInteracao = null;
    var timeoutLoop = null;
    /* A prévia animada (GIF de ~1 MB cada) só baixa quando o carrossel chega perto da tela da página.
       Antes, os cards visíveis DENTRO do carrossel animavam ao abrir a página, mesmo com os depoimentos
       lá embaixo: ~2 MB na home e na loja-luna antes de a cliente rolar (Lighthouse de 03/11/2026, item S1
       do diagnóstico do site, B209 do MenoCore). Sem IntersectionObserver, fica como era. */
    var naTela = !('IntersectionObserver' in window);

    function imagemDo(card) { return card.querySelector('img'); }

    function anima(card, ligado) {
      var img = imagemDo(card);
      if (!img) return;
      ligado = ligado && naTela;
      var alvo = ligado
        ? img.getAttribute('data-video-preview-src')
        : img.getAttribute('data-video-poster-src');
      if (alvo && img.getAttribute('src') !== alvo) img.setAttribute('src', alvo);
      img.setAttribute('loading', ligado ? 'eager' : 'lazy');
    }

    // Mobile: só o card mais à esquerda que ainda está dentro da viewport do carrossel.
    function atualizaPreviewMobile() {
      var caixa = carrossel.getBoundingClientRect();
      var maisEsquerda = -1;
      var menorDistancia = Infinity;

      cards.forEach(function (card, i) {
        var r = card.getBoundingClientRect();
        if (r.right <= caixa.left || r.left >= caixa.right) return;
        var d = Math.abs(r.left - caixa.left);
        if (d < menorDistancia) { menorDistancia = d; maisEsquerda = i; }
      });

      cards.forEach(function (card, i) { anima(card, i === maisEsquerda); });
    }

    function atualizaIndiceAutoplay() {
      var caixa = carrossel.getBoundingClientRect();
      var maisProximo = 0;
      var menorDistancia = Infinity;
      cards.forEach(function (card, i) {
        var d = Math.abs(card.getBoundingClientRect().left - caixa.left);
        if (d < menorDistancia) { menorDistancia = d; maisProximo = i; }
      });
      indiceAutoplay = maisProximo;
    }

    function aoRolar() {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        atualizaIndiceAutoplay();
        if (media.matches) atualizaPreviewMobile();
      });
    }

    function configura() {
      if (observer) { observer.disconnect(); observer = null; }
      carrossel.removeEventListener('scroll', aoRolar);
      carrossel.addEventListener('scroll', aoRolar, { passive: true });

      if (media.matches) {
        cards.forEach(function (card) { anima(card, false); });
        atualizaPreviewMobile();
        return;
      }

      if (!('IntersectionObserver' in window)) return;
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { anima(entry.target, entry.isIntersecting); });
      }, { root: carrossel, threshold: 0.5 });
      cards.forEach(function (card) { observer.observe(card); });
    }

    function pausaPorInteracao() {
      pausado = true;
      if (timeoutInteracao !== null) window.clearTimeout(timeoutInteracao);
      timeoutInteracao = window.setTimeout(function () {
        pausado = false;
        timeoutInteracao = null;
      }, INTERACTION_PAUSE_MS);
    }

    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (evento) {
      carrossel.addEventListener(evento, pausaPorInteracao, { passive: true });
    });

    function avanca() {
      if (pausado) return;
      var atual = indiceAutoplay;

      // Se a rolagem parou já na segunda cópia, volta para a primeira sem animação
      // antes de avançar — senão o carrossel andaria para fora do DOM.
      if (atual >= totalVideos) {
        atual = atual % totalVideos;
        carrossel.scrollTo({ left: cards[atual].offsetLeft, behavior: 'auto' });
      }

      var proximo = atual + 1;
      indiceAutoplay = proximo;
      carrossel.scrollTo({ left: cards[proximo].offsetLeft, behavior: 'smooth' });

      // Chegou na primeira cópia do segundo bloco: deixa a rolagem suave terminar e
      // reancora no início. Como card[7] é idêntico a card[0], o salto é invisível.
      if (proximo === totalVideos) {
        if (timeoutLoop !== null) window.clearTimeout(timeoutLoop);
        timeoutLoop = window.setTimeout(function () {
          carrossel.scrollTo({ left: cards[0].offsetLeft, behavior: 'auto' });
          indiceAutoplay = 0;
          timeoutLoop = null;
        }, LOOP_RESET_DELAY_MS);
      }
    }

    configura();
    if (!naTela) {
      new IntersectionObserver(function (entries) {
        var agora = entries[entries.length - 1].isIntersecting;
        if (agora !== naTela) { naTela = agora; configura(); }
      }, { rootMargin: '300px 0px' }).observe(carrossel);
    }
    if (media.addEventListener) media.addEventListener('change', configura);
    window.setInterval(avanca, AUTOPLAY_INTERVAL_MS);

    // Abrir o vídeo pausa o autoplay, como na origem (:186-189).
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        pausaPorInteracao();
        abrirModalVideo({
          id: card.getAttribute('data-video-id'),
          name: card.getAttribute('data-video-name'),
          hls: card.getAttribute('data-video-hls'),
          poster: card.getAttribute('data-video-poster')
        });
      });
    });
  }

  /* ==========================================================================
     12. Carrossel de prints + lightbox
     ==========================================================================
     Máquina de estados diferente da do carrossel de vídeos, e a diferença é deliberada
     (components/WhatsAppPrintCarousel.js):

     - O array é TRIPLICADO (copies = 3), não duplicado → 12 cards para 4 prints.
     - O índice ativo é mantido na janela [total, total*2) — a cópia DO MEIO. Sair dessa
       janela reancora antes de avançar, e 700 ms depois do fim reancora de novo.
       É isso que faz o loop nunca travar na borda, nos dois sentidos.
     - `centerCard` CENTRALIZA o card na viewport; o de vídeos alinha à esquerda.
     ========================================================================== */
  var PRINT_AUTOPLAY_MS = 5000;
  var PRINT_PAUSE_MS = 15000;
  var PRINT_LOOP_RESET_MS = 700;
  var LIGHTBOX_ANIMATION_MS = 280;

  function initPrintCarousel() {
    var viewport = document.querySelector('[data-print-carousel]');
    if (!viewport) return;

    var total = parseInt(viewport.getAttribute('data-print-total'), 10) || 0;
    if (total < 2) return;

    var cards = Array.prototype.slice.call(viewport.querySelectorAll('[data-whatsapp-index]'));
    var ativo = parseInt(viewport.getAttribute('data-print-initial'), 10) || total;
    var pausado = false;
    var timeoutInteracao = null;
    var timeoutLoop = null;

    function centraliza(indice, comportamento) {
      var card = viewport.querySelector('[data-whatsapp-index="' + indice + '"]');
      if (!card) return;
      viewport.scrollTo({
        left: card.offsetLeft - (viewport.clientWidth - card.offsetWidth) / 2,
        behavior: comportamento || 'smooth'
      });
    }

    function ativa(indice) {
      ativo = indice;
      cards.forEach(function (card) {
        var i = parseInt(card.getAttribute('data-whatsapp-index'), 10);
        card.classList.toggle('whatsapp-print-carousel__card--active', i === indice);
      });
    }

    function pausa() {
      pausado = true;
      if (timeoutInteracao !== null) window.clearTimeout(timeoutInteracao);
      timeoutInteracao = window.setTimeout(function () {
        pausado = false;
        timeoutInteracao = null;
      }, PRINT_PAUSE_MS);
    }

    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (evento) {
      viewport.addEventListener(evento, pausa, { passive: true });
    });

    /* ------------------------------------------------------------------------
       SINCRONIZAÇÃO DE VOLTA — porte de WhatsAppPrintCarousel.js:85-115

       Sem isto, `ativo` só muda quando o autoplay o move. Arrastar o carrossel com o dedo
       ou com o trackpad rola os cards mas NÃO troca a classe `--active`: o card que ficou
       no centro continua reduzido e translúcido, e o que saiu de cena continua em 100%.
       Aguardar o autoplay funcionava; arrastar, não. Era exatamente esse o relato.

       O card ativo é o de centro mais próximo do centro da viewport — a mesma conta da
       origem, e a mesma que o carrossel de produtos da home já fazia (motor 15). Dentro de
       um requestAnimationFrame para não medir layout a cada pixel de scroll.

       Não precisa da guarda de scroll programático que o carrossel de produtos tem: lá o
       evento `scroll` conta como interação e pausaria o autoplay sozinho. Aqui quem pausa é
       pointerdown/touchstart/wheel/keydown, e `scroll` não está na lista — o smooth scroll
       do próprio autoplay atravessa esta função e converge para o card de destino, que é o
       que se quer.
       ------------------------------------------------------------------------ */
    var frameSync = null;

    function sincronizaAtivo() {
      frameSync = null;
      var centroViewport = viewport.scrollLeft + viewport.clientWidth / 2;
      var maisProximo = ativo;
      var menorDistancia = Infinity;

      cards.forEach(function (card) {
        var centroCard = card.offsetLeft + card.offsetWidth / 2;
        var distancia = Math.abs(centroCard - centroViewport);
        if (distancia < menorDistancia) {
          menorDistancia = distancia;
          maisProximo = parseInt(card.getAttribute('data-whatsapp-index'), 10);
        }
      });

      if (maisProximo !== ativo) ativa(maisProximo);
    }

    viewport.addEventListener('scroll', function () {
      if (frameSync !== null) return;
      frameSync = window.requestAnimationFrame(sincronizaAtivo);
    }, { passive: true });

    window.setInterval(function () {
      if (pausado) return;
      var atual = ativo;

      // Fora da cópia do meio? Reancora lá antes de avançar.
      if (atual < total || atual >= total * 2) {
        atual = total + (atual % total);
        ativa(atual);
        centraliza(atual, 'auto');
      }

      var proximo = atual + 1;
      ativa(proximo);
      centraliza(proximo);

      if (proximo >= total * 2) {
        if (timeoutLoop !== null) window.clearTimeout(timeoutLoop);
        timeoutLoop = window.setTimeout(function () {
          var reset = total + (proximo % total);
          ativa(reset);
          centraliza(reset, 'auto');
          timeoutLoop = null;
        }, PRINT_LOOP_RESET_MS);
      }
    }, PRINT_AUTOPLAY_MS);

    // Posição inicial: cópia do meio, sem animação.
    ativa(ativo);
    centraliza(ativo, 'auto');

    initPrintLightbox(pausa);
  }

  /**
   * Lightbox dos prints.
   *
   * Nasce em document.body (na origem é createPortal, :233) e é o único elemento da página
   * fora do wrapper .vita-page — por isso o mini-reset do CSS é global, senão ele perderia
   * o box-sizing.
   *
   * A animação de fechamento de 280 ms via classe --closing não é enfeite: sem ela o
   * lightbox pisca ao fechar (:7,58-65,235). O foco vai para o botão de fechar ao abrir e
   * volta para quem abriu ao fechar.
   */
  function initPrintLightbox(pausaAutoplay) {
    var gatilhos = Array.prototype.slice.call(document.querySelectorAll('[data-print-zoom]'));
    if (!gatilhos.length) return;

    var overlay = document.createElement('div');
    overlay.className = 'whatsapp-print-lightbox';
    overlay.hidden = true;
    overlay.style.display = 'none';
    // Estrutura EXATA de components/WhatsAppPrintCarousel.js:233-272. As classes importam:
    // é `__image-button img` que limita o tamanho da imagem (loja-vita.css:1326). Inventar
    // nome de classe aqui deixa a imagem sem restrição e ela abre maior que a tela.
    overlay.innerHTML =
      '<div class="whatsapp-print-lightbox__dialog" role="dialog" aria-modal="true">' +
      '<button type="button" class="whatsapp-print-lightbox__close" aria-label="Fechar depoimento ampliado">×</button>' +
      '<button type="button" class="whatsapp-print-lightbox__image-button" aria-label="Reduzir imagem e fechar">' +
      '<img src="" alt="" width="720" height="1356" decoding="async">' +
      '</button>' +
      '<p class="whatsapp-print-lightbox__hint">Toque novamente para fechar</p>' +
      '</div>';
    document.body.appendChild(overlay);

    var dialogo = overlay.querySelector('.whatsapp-print-lightbox__dialog');
    var imagem = overlay.querySelector('.whatsapp-print-lightbox__image-button img');
    var botaoImagem = overlay.querySelector('.whatsapp-print-lightbox__image-button');
    var fechar = overlay.querySelector('.whatsapp-print-lightbox__close');
    var quemAbriu = null;
    var timeoutFechamento = null;

    function abrir(gatilho) {
      quemAbriu = gatilho;
      var nome = gatilho.getAttribute('data-print-name') || '';
      imagem.src = gatilho.getAttribute('data-print-src') || '';
      imagem.alt = 'Depoimento ampliado de ' + nome;
      if (dialogo) dialogo.setAttribute('aria-label', 'Depoimento ampliado de ' + nome);
      overlay.hidden = false;
      overlay.style.display = '';
      overlay.classList.remove('whatsapp-print-lightbox--closing');
      document.body.style.overflow = 'hidden';
      if (fechar) fechar.focus();
      if (pausaAutoplay) pausaAutoplay();
    }

    function fecha() {
      if (timeoutFechamento !== null) return;
      overlay.classList.add('whatsapp-print-lightbox--closing');
      timeoutFechamento = window.setTimeout(function () {
        overlay.hidden = true;
        overlay.style.display = 'none';
        overlay.classList.remove('whatsapp-print-lightbox--closing');
        document.body.style.overflow = '';
        if (quemAbriu) { quemAbriu.focus(); quemAbriu = null; }
        timeoutFechamento = null;
      }, LIGHTBOX_ANIMATION_MS);
    }

    gatilhos.forEach(function (gatilho) {
      gatilho.addEventListener('click', function () { abrir(gatilho); });
    });
    if (fechar) fechar.addEventListener('click', fecha);
    // Clicar na própria imagem também fecha — é botão na origem, não <img> solta.
    if (botaoImagem) botaoImagem.addEventListener('click', fecha);
    // Fecha por alvo, não por stopPropagation — é assim na origem (:236-238).
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) fecha();
    });
  }

  /* ==========================================================================
     13. Validação de telefone — porte de lib/phone.js
     ==========================================================================
     NÃO é "telefone BR" genérico. As três regras abaixo existem porque o
     libphonenumber aprova números que são só preenchimento de formulário, e porque o
     Brasil tem uma herança de 8 dígitos. Uma regex divergente aceitaria lead que o braço
     Vercel recusa — e taxa de lead é métrica comparada pelo teste.
     ========================================================================== */
  var PHONE_ERROR_MESSAGE = 'Informe um número de WhatsApp válido com DDD.';

  /**
   * Padrão falso: 99999-9999, 91234-5678, 98765-4321. Sintaticamente válidos, e ninguém
   * digita isso de verdade. lib/phone.js:31-44 — olha os ÚLTIMOS 8 dígitos.
   */
  function ehPadraoFalso(digitos) {
    var ultimos8 = digitos.slice(-8);
    if (ultimos8.length < 8) return false;
    if (/^(\d)\1+$/.test(ultimos8)) return true;

    var crescente = true;
    var decrescente = true;
    for (var i = 1; i < ultimos8.length; i++) {
      var delta = Number(ultimos8[i]) - Number(ultimos8[i - 1]);
      if (delta !== 1) crescente = false;
      if (delta !== -1) decrescente = false;
    }
    return crescente || decrescente;
  }

  /**
   * Normaliza para os dígitos nacionais, reproduzindo lib/phone.js:48-54 e :81-83:
   * descarta DDI 55 duplicado, tira o trunk '0', e reinsere o 9º dígito em celular
   * legado de 8 — o E.164 real sempre tem 9.
   */
  function normalizaTelefoneBR(bruto) {
    var digitos = (bruto || '').replace(/\D/g, '');
    while (digitos.length > 11 && digitos.indexOf('55') === 0) digitos = digitos.slice(2);
    digitos = digitos.replace(/^0+/, '');

    // 10 dígitos com o primeiro do número entre 6 e 9 = celular legado sem o 9.
    if (digitos.length === 10 && /^[6-9]/.test(digitos.charAt(2))) {
      digitos = digitos.slice(0, 2) + '9' + digitos.slice(2);
    }
    return digitos;
  }

  function telefoneValido(bruto) {
    var d = normalizaTelefoneBR(bruto);
    if (d.length !== 10 && d.length !== 11) return false;

    var ddd = Number(d.slice(0, 2));
    if (ddd < 11 || ddd > 99) return false;

    // Celular brasileiro: 11 dígitos e o terceiro é 9.
    if (d.length === 11 && d.charAt(2) !== '9') return false;

    return !ehPadraoFalso(d);
  }

  /* ==========================================================================
     14. Modal de lead do WhatsApp
     ==========================================================================
     Três coisas que o porte NÃO pode simplificar:

     1. O fetch usa URL ABSOLUTA. No tema, '/api/leads' resolveria para a própria loja
        Shopify e daria 404 em HTML, antes de qualquer preflight de CORS — o CORS de
        app/api/leads/route.js só entra em cena depois de a URL apontar para o host certo.
     2. `whatsapp_click` dispara DEPOIS do fetch (WhatsAppLeadModal.js:67 vs :73). Se a
        rede cair, nenhum evento é emitido. É defeito A5, reproduzido.
     3. `lead_complete` só em HTTP 201, não em qualquer response.ok (:77). Sem essa
        condição o evento infla em todo lead repetido.

     Até 25/09/2026 `lp_session_id` NÃO ia no payload: não havia lp-track neste braço
     (assimetria declarada). Desde então vai, quando o aera-lp-track.js já abriu a sessão
     (P25/D31 do dossiê MenoCore docs/implementations/2026-09-23-lp-ads-kit-alta-performance/):
     a /api/leads já faz bindSession com ele, e a sessão ganha dona no CRM, como na Vercel.
     E o `whatsapp_modal_open` pós-fetch vira `whatsapp_click` no lp-track, que é o nome que
     a origem emite ali (WhatsAppLeadModal.js:73) — ver o terceiro argumento do pushEvent.
     ========================================================================== */
  var LEADS_ENDPOINT = 'https://loja.aera40mais.com.br/api/leads';
  var WHATSAPP_NUMBER = CFG.lead.whatsapp_number;
  var PRODUCT_NAME = CFG.lead.product_name;
  var FORM_NAME = CFG.lead.form_name;

  /**
   * Máscara BR do campo de WhatsApp: `(48) 99146-1065`.
   *
   * Na origem quem faz isso é a react-phone-number-input, com `limitMaxLength` ligado
   * (components/WhatsAppLeadModal.js:126): ela formata enquanto se digita e PARA no
   * comprimento máximo do país. O porte tinha só um `maxlength` no HTML, que conta
   * caracteres e não dígitos — dava para digitar 16 algarismos e o campo aceitava, porque
   * a validação só acontecia no submit. Isto aqui é paridade, não invenção.
   *
   * Aceita os dois formatos que o Brasil ainda tem: 10 dígitos (fixo, `(48) 3216-1065`) e
   * 11 (celular). O parêntese só fecha quando o DDD está completo, senão apagar o segundo
   * dígito do DDD ficaria preso atrás do `) `.
   */
  function formataTelefoneBR(bruto) {
    var d = (bruto || '').replace(/\D/g, '').slice(0, 11);
    if (d.length === 0) return '';
    if (d.length <= 2) return '(' + d;
    var ddd = '(' + d.slice(0, 2) + ') ';
    var resto = d.slice(2);
    if (resto.length <= 4) return ddd + resto;
    // 11 dígitos = celular, o traço cai depois do quinto; 10 = fixo, depois do quarto.
    var corte = d.length > 10 ? 5 : 4;
    return ddd + resto.slice(0, corte) + '-' + resto.slice(corte);
  }

  /**
   * Aplica a máscara preservando o cursor. Sem o ajuste de `selectionStart`, editar o meio
   * do número jogaria o cursor para o fim a cada tecla — e o campo é preenchido no celular,
   * onde recuperar a posição é pior ainda.
   */
  function ligaMascaraTelefone(campo) {
    if (!campo) return;

    function aplica() {
      var antes = campo.value;
      var fim = campo.selectionStart === antes.length;
      var digitosAteCursor = antes.slice(0, campo.selectionStart || 0).replace(/\D/g, '').length;
      var depois = formataTelefoneBR(antes);
      if (depois === antes) return;
      campo.value = depois;

      if (fim) return;
      var contados = 0;
      var pos = depois.length;
      for (var i = 0; i < depois.length; i++) {
        if (/\d/.test(depois[i])) contados++;
        if (contados === digitosAteCursor) { pos = i + 1; break; }
      }
      try { campo.setSelectionRange(pos, pos); } catch (err) { /* input sem seleção */ }
    }

    campo.addEventListener('input', aplica);
    campo.addEventListener('blur', aplica);
  }

  function initLeadModal() {
    var overlay = document.querySelector('[data-lead-modal]');
    if (!overlay) return;

    var form = overlay.querySelector('[data-lead-form]');
    var erro = overlay.querySelector('[data-lead-error]');
    var submit = overlay.querySelector('[data-lead-submit]');
    var campoNome = overlay.querySelector('#wa-name');
    var campoEmail = overlay.querySelector('#wa-email');
    var campoTelefone = overlay.querySelector('#wa-phone');
    var enviando = false;

    ligaMascaraTelefone(campoTelefone);

    overlay.hidden = true;
    overlay.style.display = 'none';

    function mostraErro(mensagem) {
      if (!erro) return;
      erro.textContent = mensagem || '';
      erro.hidden = !mensagem;
    }

    /* ------------------------------------------------------------------------
       TECLADO DO CELULAR — o overlay passa a valer a faixa VISÍVEL, não a página

       O modal é `position: fixed`, e no iOS o teclado virtual **não encolhe** o
       layout viewport: encolhe só o `visualViewport`. O overlay continuava com a
       altura da página inteira, o conteúdo centralizado caía atrás do teclado, e
       como não havia transbordo não havia o que rolar — digitava-se às cegas.

       Aqui o JS escreve a altura e o deslocamento reais em duas custom properties
       que o CSS consome (`--wa-vvh` / `--wa-vvtop`). O overlay passa a ocupar
       exatamente a faixa acima do teclado; o `overflow-y: auto` do CSS faz o resto.

       Só roda com o modal ABERTO: os listeners são presos no `abrir()` e soltos no
       `fechar()`. Um listener permanente de `visualViewport` dispararia em todo
       scroll de página no mobile, de graça, o tempo todo.

       Degrada em silêncio: sem `window.visualViewport` (navegador antigo), as
       variáveis nunca são escritas e o CSS cai nos defaults `100%` / `0px`, que é
       exatamente o comportamento anterior — nada piora.
       ------------------------------------------------------------------------ */
    function ajustaAoTeclado() {
      var vv = window.visualViewport;
      if (!vv) return;
      overlay.style.setProperty('--wa-vvh', vv.height + 'px');
      overlay.style.setProperty('--wa-vvtop', vv.offsetTop + 'px');
    }

    function limpaAjusteTeclado() {
      overlay.style.removeProperty('--wa-vvh');
      overlay.style.removeProperty('--wa-vvtop');
    }

    function ligaTeclado() {
      if (!window.visualViewport) return;
      window.visualViewport.addEventListener('resize', ajustaAoTeclado);
      window.visualViewport.addEventListener('scroll', ajustaAoTeclado);
      ajustaAoTeclado();
    }

    function desligaTeclado() {
      if (!window.visualViewport) return;
      window.visualViewport.removeEventListener('resize', ajustaAoTeclado);
      window.visualViewport.removeEventListener('scroll', ajustaAoTeclado);
      limpaAjusteTeclado();
    }

    /*
      Trazer o campo focado para a vista. O `visualViewport` já garante que o campo
      ESTÁ na área rolável; isto garante que ele esteja VISÍVEL nela sem a visitante
      precisar rolar à mão.

      Os 300 ms não são superstição: no iOS o evento de `focus` chega ANTES de o
      teclado terminar de subir, e medir a essa altura devolve a geometria antiga.
      É o mesmo motivo de o ajuste ser refeito no `resize` do visualViewport.
    */
    /*
      A classe `--teclado` liga a folga de rolagem de 45vh e encolhe o card. Ela existe
      porque o ajuste por visualViewport depende de o navegador reportar a faixa visível,
      e o navegador embutido do Instagram — por onde entra boa parte do tráfego pago —
      nem sempre reporta. Sem folga não há transbordo, sem transbordo não há rolagem, e o
      botão de envio fica atrás do teclado.

      `focusout` seguido de `focusin` é o que acontece ao pular de um campo para o outro,
      e retirar a classe nesse intervalo faria o card pular de tamanho a cada troca. Por
      isso a remoção é adiada e cancelada se o foco voltar para dentro do formulário.
    */
    var timeoutTeclado = null;

    function marcaTeclado(ligado) {
      if (ligado) {
        if (timeoutTeclado !== null) { window.clearTimeout(timeoutTeclado); timeoutTeclado = null; }
        overlay.classList.add('v2-wa-modal--teclado');
      } else {
        if (timeoutTeclado !== null) window.clearTimeout(timeoutTeclado);
        timeoutTeclado = window.setTimeout(function () {
          overlay.classList.remove('v2-wa-modal--teclado');
          timeoutTeclado = null;
        }, 120);
      }
    }

    overlay.addEventListener('focusin', function () { marcaTeclado(true); });
    overlay.addEventListener('focusout', function () { marcaTeclado(false); });

    [campoNome, campoEmail, campoTelefone].forEach(function (campo) {
      if (!campo) return;
      campo.addEventListener('focus', function () {
        /*
          300 ms: no iOS o `focus` chega ANTES de o teclado terminar de subir, e medir a
          essa altura devolve a geometria antiga. O `block: 'center'` deixa o campo no
          meio da faixa, o que mantém o botão de envio a uma rolagem curta de distância.
        */
        window.setTimeout(function () {
          if (campo.scrollIntoView) campo.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 300);
      });
    });

    function abrir() {
      mostraErro('');
      overlay.hidden = false;
      overlay.style.display = '';
      ligaTeclado();
      if (campoNome) campoNome.focus();
      pushEvent('whatsapp_modal_open');
    }

    function fechar() {
      desligaTeclado();
      if (timeoutTeclado !== null) { window.clearTimeout(timeoutTeclado); timeoutTeclado = null; }
      overlay.classList.remove('v2-wa-modal--teclado');
      overlay.scrollTop = 0;
      overlay.hidden = true;
      overlay.style.display = 'none';
    }

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) fechar();
    });
    var conteudo = overlay.querySelector('[data-modal-content]');
    if (conteudo) conteudo.addEventListener('click', function (e) { e.stopPropagation(); });
    var botaoFechar = overlay.querySelector('[data-lead-close]');
    if (botaoFechar) botaoFechar.addEventListener('click', fechar);

    /* Desde 02/11/2026 os gatilhos `[data-whatsapp-modal]` não abrem mais este modal: abrem o
       WhatsApp direto (initWhatsAppDireto, B209 do MenoCore, D3 e D5 de
       docs/implementations/2026-11-02-diagnostico-do-site/DECISOES.md). O modal e o envio do
       formulário ficam no código, sem gatilho, para voltar com uma linha se o Ygor pedir. */
    void abrir;

    if (!form) return;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (enviando) return;

      mostraErro('');
      var nome = (campoNome && campoNome.value || '').trim();
      var email = (campoEmail && campoEmail.value || '').trim();
      var telefone = (campoTelefone && campoTelefone.value || '').trim();

      if (!nome || !email || !telefone) {
        mostraErro('Por favor, preencha todos os campos.');
        return;
      }
      // Nome completo = pelo menos duas palavras (WhatsAppLeadModal.js:42-47).
      if (nome.split(/\s+/).length < 2) {
        mostraErro('Por favor, insira seu nome completo.');
        return;
      }
      if (!telefoneValido(telefone)) {
        mostraErro(PHONE_ERROR_MESSAGE);
        return;
      }

      enviando = true;
      if (submit) { submit.disabled = true; submit.textContent = 'Processando...'; }

      var params = new URLSearchParams(window.location.search);
      var payload = {
        display_name: nome,
        email: email,
        phone_number: '+55' + normalizaTelefoneBR(telefone),
        user_origin: 'site_aera40mais',
        page_url: window.location.href,
        form_name: FORM_NAME
      };
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = params.get(k);
        if (v) payload[k] = v;
      });
      // Sessão do lp-track (25/09/2026): a /api/leads faz bindSession quando o campo vem.
      if (window.aeraLpTrack) {
        var idsSessao = window.aeraLpTrack.storedIds();
        if (idsSessao.lp_session_id) payload.lp_session_id = idsSessao.lp_session_id;
      }

      fetch(LEADS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (response) {
        // Depois do fetch, como na origem: rede caída não emite evento nenhum. No GTM o nome
        // é `whatsapp_modal_open` (o hook mapeia whatsapp_click e whatsapp_modal_open para o
        // mesmo push); no lp-track é `whatsapp_click`, como em WhatsAppLeadModal.js:73 — e o
        // servidor grava `whatsapp_clicked: true` só com esse nome. `position: 'modal'` diz de
        // onde veio o clique, como todo `whatsapp_click` (D64 do dossiê MenoCore).
        pushEvent('whatsapp_modal_open', { success: response.ok, position: 'modal' }, 'whatsapp_click');

        if (!response.ok) {
          return response.json().then(function (data) {
            mostraErro((data && data.error) || 'Erro ao processar. Tente novamente.');
          }).catch(function () {
            mostraErro('Erro ao processar. Tente novamente.');
          });
        }

        if (response.status === 201) pushEvent('lead_complete');

        var link = 'https://api.whatsapp.com/send/?phone=' + WHATSAPP_NUMBER +
          '&text=Ol%C3%A1%2C+tenho+uma+d%C3%BAvida+sobre+o+' + encodeURIComponent(PRODUCT_NAME);
        window.open(link, '_blank');

        fechar();
        if (campoNome) campoNome.value = '';
        if (campoEmail) campoEmail.value = '';
        if (campoTelefone) campoTelefone.value = '';
        return null;
      }).catch(function () {
        mostraErro('Erro de conexão. Tente novamente.');
      }).then(function () {
        enviando = false;
        if (submit) { submit.disabled = false; submit.textContent = 'Iniciar Conversa no WhatsApp'; }
      });
    });
  }

  /* ==========================================================================
     15. Vitrine /loja — carrossel de produtos e CTAs por produto
     ==========================================================================
     Porte de pages/loja/index.js:82-171. Só roda na home: sem `[data-product-carousel]`
     no DOM, todo o bloco é inerte.

     Quatro comportamentos que o React coordenava por estado e aqui precisam ser
     explícitos:

     1. **Autoplay só no mobile.** A origem observa `matchMedia('(max-width: 639px)')`
        (:84) e o intervalo de 5 s só existe quando `isMobileProducts` é true (:98-104).
        No desktop os três cards aparecem lado a lado e não há o que rodar. O listener de
        `change` fica ativo: girar o aparelho liga ou desliga o autoplay sem recarregar.

     2. **Pausa de 30 s a qualquer interação** (:129-141). Toque, ponteiro, roda do mouse
        ou teclado param o autoplay e agendam a retomada. Cada nova interação REINICIA a
        contagem — por isso o timeout é limpo antes de reagendar.

     3. **Guarda de scroll programático** (:117-121). Quando o JS rola o carrossel para o
        card escolhido, o evento `scroll` que isso dispara NÃO pode ser lido como
        interação da visitante — senão o autoplay se auto-pausaria a cada ciclo e nunca
        mais avançaria. A origem resolve com uma flag e uma janela de 600 ms; o mesmo aqui.

     4. **Sincronização de volta** (:143-166): quando a visitante arrasta, o dot ativo é
        recalculado achando o card mais próximo da borda de snap, dentro de um rAF para
        não medir layout a cada pixel de scroll.

     `scrollTo({behavior:'smooth'})` é o mesmo da origem. Onde não houver suporte, o salto
     é instantâneo — degrada para o comportamento certo, sem quebrar.
     ========================================================================== */
  var PRODUCT_AUTOPLAY_MS = 5000;
  var PRODUCT_INTERACTION_PAUSE_MS = 15000;

  function initProductCarousel() {
    var carrossel = document.querySelector('[data-product-carousel]');
    if (!carrossel) return;

    var cards = Array.prototype.slice.call(carrossel.children);
    var dots = Array.prototype.slice.call(document.querySelectorAll('[data-product-dot]'));
    if (!cards.length) return;

    var idx = 0;
    var autoplayAtivo = true;
    var ehMobile = false;
    var timerAutoplay = null;
    var timeoutPausa = null;
    var frameScroll = null;
    var scrollProgramatico = false;
    var timeoutProgramatico = null;

    var media = window.matchMedia('(max-width: 639px)');

    function pintaDots() {
      dots.forEach(function (dot, i) {
        var ativo = i === idx;
        dot.classList.toggle('lh-product-dot--active', ativo);
        if (ativo) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function rolaAte(i) {
      if (!ehMobile) return;
      var card = cards[i];
      if (!card) return;

      var retanguloCarrossel = carrossel.getBoundingClientRect();
      var retanguloCard = card.getBoundingClientRect();
      var recuoEsquerdo = parseFloat(window.getComputedStyle(carrossel).paddingLeft) || 0;
      var alvo = carrossel.scrollLeft + retanguloCard.left - retanguloCarrossel.left - recuoEsquerdo;

      scrollProgramatico = true;
      if (timeoutProgramatico !== null) window.clearTimeout(timeoutProgramatico);
      try {
        carrossel.scrollTo({ left: alvo, behavior: 'smooth' });
      } catch (err) {
        carrossel.scrollLeft = alvo;
      }
      timeoutProgramatico = window.setTimeout(function () {
        scrollProgramatico = false;
        timeoutProgramatico = null;
      }, 600);
    }

    function vaiPara(i) {
      idx = i;
      pintaDots();
      rolaAte(i);
    }

    function paraAutoplay() {
      if (timerAutoplay !== null) {
        window.clearInterval(timerAutoplay);
        timerAutoplay = null;
      }
    }

    function ligaAutoplay() {
      paraAutoplay();
      if (!ehMobile || !autoplayAtivo) return;
      timerAutoplay = window.setInterval(function () {
        vaiPara((idx + 1) % cards.length);
      }, PRODUCT_AUTOPLAY_MS);
    }

    function pausaAutoplay() {
      scrollProgramatico = false;
      if (timeoutProgramatico !== null) {
        window.clearTimeout(timeoutProgramatico);
        timeoutProgramatico = null;
      }
      autoplayAtivo = false;
      paraAutoplay();
      if (timeoutPausa !== null) window.clearTimeout(timeoutPausa);
      timeoutPausa = window.setTimeout(function () {
        autoplayAtivo = true;
        timeoutPausa = null;
        ligaAutoplay();
      }, PRODUCT_INTERACTION_PAUSE_MS);
    }

    function aoRolar() {
      if (!ehMobile || scrollProgramatico || frameScroll !== null) return;
      frameScroll = window.requestAnimationFrame(function () {
        frameScroll = null;
        var retanguloCarrossel = carrossel.getBoundingClientRect();
        var recuoEsquerdo = parseFloat(window.getComputedStyle(carrossel).paddingLeft) || 0;
        var posicaoSnap = retanguloCarrossel.left + recuoEsquerdo;
        var maisProximo = 0;
        var menorDistancia = Infinity;
        cards.forEach(function (card, i) {
          var distancia = Math.abs(card.getBoundingClientRect().left - posicaoSnap);
          if (distancia < menorDistancia) {
            menorDistancia = distancia;
            maisProximo = i;
          }
        });
        if (maisProximo !== idx) {
          idx = maisProximo;
          pintaDots();
        }
      });
    }

    function atualizaMobile() {
      ehMobile = media.matches;
      ligaAutoplay();
    }

    carrossel.addEventListener('scroll', aoRolar, { passive: true });
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (evento) {
      carrossel.addEventListener(evento, pausaAutoplay, { passive: true });
    });

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        vaiPara(i);
        pausaAutoplay();
      });
    });

    if (media.addEventListener) {
      media.addEventListener('change', atualizaMobile);
    } else if (media.addListener) {
      media.addListener(atualizaMobile);
    }

    atualizaMobile();
    pintaDots();
  }

  /* ==========================================================================
     16. Vitrine /loja — cliques de produto e de checkout
     ==========================================================================
     Porte de pages/loja/index.js:168-176. Difere do initBuyButtons (motor 5) em três
     pontos, e por isso é motor próprio em vez de um `if` lá dentro:

     - o destino do checkout é do CARD, não de um `state.offer` global;
     - o clique de compra emite `checkout_button_clicked`, não o `click_to_checkout` da
       LP de anúncio. A origem (:173-174) emite também `checkout_clicked`, sinônimo que
       nenhuma tag do GTM nem painel de `lp_sessions` lê; o tema deixou de mandá-lo (D65 do
       dossiê MenoCore docs/implementations/2026-09-23-lp-ads-kit-alta-performance/);
     - existe um segundo CTA, "Ver detalhes", que emite `product_click` e navega para a
       LP do produto — a home é a única página do braço com navegação interna real.

     **Sem o Promise.race de 300 ms da LP de anúncio**, e isso é fiel: a origem não o tem
     aqui. O `<a href>` navega sozinho, o push acontece no mesmo tick e a corrida com o
     unload é a mesma dos dois lados. Ver ETAPA2_PORTE.md §5.13 sobre por que a LP paga
     precisa da espera e a vitrine não.
     ========================================================================== */
  function initHomeCards() {
    document.querySelectorAll('[data-product-link]').forEach(function (link) {
      link.addEventListener('click', function () {
        pushEvent('product_click', {
          product: link.getAttribute('data-product') || '',
          position: 'home_card'
        });
      });
    });

    document.querySelectorAll('[data-product-checkout]').forEach(function (botao) {
      botao.addEventListener('click', function (evento) {
        var produto = botao.getAttribute('data-product') || '';
        pushEvent('checkout_button_clicked', {
          offer: produto,
          product: produto,
          position: 'home_card'
        });

        var bruto = botao.getAttribute('data-checkout-url');
        if (!bruto) return;
        evento.preventDefault();
        window.location.href = buildCheckoutUrl(bruto);
      });
    });
  }

  /* ==========================================================================
     17. Régua de âncoras — só a /loja-vita tem
     ==========================================================================
     Porte de pages/loja-vita/index.js:592-596. Cinco links que cancelam a navegação,
     rolam suave até a seção e emitem `anchor_click`.

     `anchor_click` é um dos 9 eventos de GA4 que o contêiner GTM já escuta — o nome NÃO é
     invenção deste porte. No braço Vercel ele nasce da mesma régua, na mesma página; a
     /ads-luna e a /loja-luna simplesmente não têm o bloco, e por isso nunca o emitem.

     `preventDefault` antes de qualquer coisa: sem ele o browser pula direto pela âncora e
     o scroll suave nunca acontece. Se `querySelector` não achar o alvo, nada rola — e o
     evento também não é emitido, porque um clique que não navegou não é uma navegação.
     ========================================================================== */
  function initAnchorLinks() {
    document.querySelectorAll('[data-anchor-link]').forEach(function (link) {
      link.addEventListener('click', function (evento) {
        var alvo = link.getAttribute('href') || '';
        var destino = alvo.charAt(0) === '#' ? document.querySelector(alvo) : null;
        if (!destino) return;
        evento.preventDefault();
        destino.scrollIntoView({ behavior: 'smooth', block: 'start' });
        pushEvent('anchor_click', { target: alvo });
      });
    });
  }

  /* ==========================================================================
     WhatsApp direto (02/11/2026, B209 do MenoCore, D3 e D5 de
     docs/implementations/2026-11-02-diagnostico-do-site/DECISOES.md)
     ==========================================================================
     Todo `[data-whatsapp-modal]` (rodapé, FAQ, botão flutuante) abre o WhatsApp direto, sem o
     formulário do modal. O número é o 7885; o link que traz `data-whatsapp-number` abre o dele
     (o "(48) 9 9146-1065" do rodapé abre o 1065, D5). O href é posto aqui, com `target=_blank`,
     e o clique só registra: quem navega é o próprio link, então o registro nunca segura a
     cliente. O registro é `whatsapp_click` com `position` e `numero` (catálogo de eventos do
     MenoCore); no GTM o nome também é `whatsapp_click`, e não `whatsapp_modal_open`, porque este
     clique não é o envio do formulário que o GTM conhecia por esse nome.
     ========================================================================== */
  var WHATSAPP_DIRETO = '5548991947885';

  function linkWhatsApp(numero) {
    return 'https://api.whatsapp.com/send/?phone=' + numero +
      '&text=' + encodeURIComponent('Olá, tenho uma dúvida sobre o ' + (CFG.lead.product_name || 'AERA40+'));
  }

  function initWhatsAppDireto() {
    document.querySelectorAll('[data-whatsapp-modal]').forEach(function (link) {
      var numero = link.getAttribute('data-whatsapp-number') || WHATSAPP_DIRETO;
      link.setAttribute('href', linkWhatsApp(numero));
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.addEventListener('click', function () {
        pushEvent('whatsapp_click', {
          position: link.getAttribute('data-whatsapp-position') || 'pagina',
          numero: numero
        });
      });
    });
  }

  /* ==========================================================================
     Campo de CEP do cartão de preço (02/11/2026, B209, D1 e D8 do mesmo dossiê)
     ==========================================================================
     Pergunta à rota GET /api/frete/prazo do MenoCore e escreve "Previsão de entrega no dia <data>,
     <dia da semana> (N dias úteis)". A rota aplica a regra (Loggi quando atende, senão a mais
     barata, +1 dia útil de postagem); aqui só se mostra. É previsão, não promessa: quem entrega é a
     transportadora, e ela descumpre prazo (o Ygor, 02/11: "a gente não quer garantir algo que está
     fora do nosso controle"). Sem "Frete grátis" na frase, que o selo do cartão e a linha de baixo
     já dizem, e sem a caixa "Previsão de chegada", que o campo substituiu (o Ygor, 02/11).
     Falha de rede ou da rota: a frase pede para tentar de novo.
     ========================================================================== */
  var FRETE_PRAZO_ENDPOINT = 'https://loja.aera40mais.com.br/api/frete/prazo';
  var CEP_GUARDADO = 'aera_cep';
  var DIAS_DA_SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];

  /* A data em que chega: hoje mais N dias úteis (pula sábado e domingo; feriado não entra na
     conta). É a pergunta da cliente, "quando chega na minha casa?" (o Ygor, 02/11): o prazo em
     dias úteis vira um dia do calendário. */
  function dataDeChegada(diasUteis) {
    var d = new Date();
    var faltam = diasUteis;
    while (faltam > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) faltam -= 1;
    }
    return d;
  }

  function guardaCep(cep) {
    try { window.localStorage.setItem(CEP_GUARDADO, cep); } catch (e) { /* navegação privada */ }
  }

  function cepGuardado() {
    try { return window.localStorage.getItem(CEP_GUARDADO) || ''; } catch (e) { return ''; }
  }

  function initCepPrazo() {
    var caixas = document.querySelectorAll('[data-cep-prazo]');
    caixas.forEach(function (caixa) {
      var campo = caixa.querySelector('[data-cep-campo]');
      var botao = caixa.querySelector('[data-cep-botao]');
      var resultado = caixa.querySelector('[data-cep-resultado]');
      if (!campo || !botao || !resultado) return;
      var ultimo = '';

      function formata(digitos) {
        return digitos.length > 5 ? digitos.slice(0, 5) + '-' + digitos.slice(5) : digitos;
      }

      function escreve(html) { resultado.innerHTML = html; }

      /* Cada CEP calculado vira o evento `delivery_estimate` na sessão do lp-track (e no
         dataLayer), para contar quantas pessoas usam o campo (o Ygor, 02/11). `origem` separa
         quem digitou do CEP guardado de uma visita anterior, que calcula sozinho ao abrir a
         página e não é "preencher"; o CEP em si não vai. Catálogo: config/lpEventCatalog.js do
         MenoCore. */
      function registra(origem, resultadoDoCalculo, diasUteis) {
        var meta = { origem: origem, resultado: resultadoDoCalculo };
        if (diasUteis) meta.dias_uteis = diasUteis;
        try { pushEvent('delivery_estimate', meta); } catch (e) { /* tracking nunca quebra o campo */ }
      }

      function calcula(origem) {
        var cep = campo.value.replace(/\D/g, '');
        if (cep.length !== 8) {
          escreve('Digite os 8 números do CEP.');
          return;
        }
        if (cep === ultimo) return;
        ultimo = cep;
        botao.disabled = true;
        escreve('Calculando…');
        fetch(FRETE_PRAZO_ENDPOINT + '?cep=' + cep)
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
          .then(function (dados) {
            guardaCep(cep);
            if (dados && dados.atende && dados.dias_uteis) {
              var dia = dataDeChegada(dados.dias_uteis);
              var curto = dia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
              escreve('Previsão de entrega no dia <strong>' + curto + ', ' + DIAS_DA_SEMANA[dia.getDay()] +
                '</strong> (' + dados.dias_uteis + ' dias úteis).');
              registra(origem, 'prazo', dados.dias_uteis);
            } else {
              escreve('Para este CEP, o prazo aparece na próxima etapa.');
              registra(origem, 'nao_atende');
            }
          })
          .catch(function () {
            ultimo = '';
            escreve('Não deu para calcular o prazo agora; tente de novo em instantes.');
            registra(origem, 'erro');
          })
          .then(function () { botao.disabled = false; });
      }

      campo.addEventListener('input', function () {
        var digitos = campo.value.replace(/\D/g, '').slice(0, 8);
        campo.value = formata(digitos);
        if (digitos.length === 8) calcula('digitado');
      });
      botao.addEventListener('click', function () { calcula('digitado'); });
      campo.addEventListener('keydown', function (evento) {
        if (evento.key === 'Enter') { evento.preventDefault(); calcula('digitado'); }
      });

      // Quem já calculou numa visita anterior vê a data do CEP dela sem digitar de novo.
      var guardado = cepGuardado();
      if (guardado.length === 8 && caixa === caixas[0]) {
        campo.value = formata(guardado);
        calcula('guardado');
      }
    });
  }

  function init() {
    garantirCookiesMeta(); // antes do GTM: o pixel encontra os cookies ao carregar (seção 4a)
    initGtm();
    initClarity();
    initScrollReveal();
    initGalleries();
    initOffers();
    initDeliveryRange();
    initBuyButtons();
    initFlavorSelectors();
    initFlavorDialog();
    initKitCrossSell();
    initSingleBuy();
    initGuaranteeCta();
    initScarcity();
    initFaq();
    initStickyBuy();
    initModals();
    initFooterLinks();
    initVideoModal();
    initVideoCarousel();
    initPrintCarousel();
    initLeadModal();
    initWhatsAppDireto();
    initCepPrazo();
    initProductCarousel();
    initHomeCards();
    initAnchorLinks();
    pushUserData();
    pushEvent('lp_page_view');
    pushViewContent();
  }

  /* ==========================================================================
     Código fixo da visitante para a Meta (external_id), desde 25/09/2026
     ==========================================================================
     A visitante da página é anônima: sem isto a Meta só recebia identificadores do navegador
     (fbp, IP, navegador) e o `external_id` aparecia em 0% dos eventos (Dataset Quality API). O
     `lp_user_id` do rastreador (aera-lp-track.js, carregado antes deste arquivo) dura entre visitas
     e é o mesmo que vai ao checkout em `metadata[lp_user_id]`, de onde o n8n o manda no Purchase.
     As tags da Meta (modelo da Stape, "data layer mapping" ligado) leem `user_data` do dataLayer e
     o passam ao `init` do pixel, que aplica o hash antes de enviar. P38 do dossiê MenoCore
     docs/implementations/2026-09-23-lp-ads-kit-alta-performance/.
     ========================================================================== */
  function pushUserData() {
    if (shouldBlockMarketing()) return;
    var uid = '';
    try {
      uid = (window.aeraLpTrack && window.aeraLpTrack.getUserId()) || '';
    } catch (err) {
      uid = '';
    }
    if (!uid) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_data: { external_id: uid } });
  }

  /* ==========================================================================
     Produto visto — Meta ViewContent e GA4 view_item com produto, preço e moeda
     ==========================================================================
     Até 25/09/2026 o `lp_view_content` saía só com `page_id`: a Meta recebia ViewContent sem
     produto, sem valor e sem moeda, e o GA4 não recebia `view_item` desta página (a tag do GTM
     escuta `view_item`). P38 e D44 do dossiê MenoCore
     docs/implementations/2026-09-23-lp-ads-kit-alta-performance/.

     Código do produto = código de estoque (SKU), o mesmo do catálogo da Meta, do Woo, da Yampi e
     do Olist (D44). Página com dois sabores manda os dois: a visitante ainda não escolheu.
     A tag "Meta - ViewContent" (modelo da Stape, "data layer mapping" ligado) lê o objeto
     `ecommerce` no formato do GA4 e monta content_ids, contents, value e currency sozinha.

     Preço: o que a página mostra no cartão (`[data-price-now]`, oferta inicial). As páginas da
     loja não têm esse campo e usam o preço cheio da tabela.
     ========================================================================== */
  var PRODUTOS_DA_PAGINA = {
    ads_kit:   { preco: 249.70, itens: [['KIT1XMARACUJA', 'AERA40+ Kit - Maracujá'], ['KIT1XFRUTASVERMELHAS', 'AERA40+ Kit - Frutas Vermelhas']] },
    loja_kit:  { preco: 249.70, itens: [['KIT1XMARACUJA', 'AERA40+ Kit - Maracujá'], ['KIT1XFRUTASVERMELHAS', 'AERA40+ Kit - Frutas Vermelhas']] },
    ads_luna:  { preco: 169.70, itens: [['LUNA1XMARACUJA', 'AERA40+ Luna - Maracujá'], ['LUNA1XFRUTASVERMELHAS', 'AERA40+ Luna - Frutas Vermelhas']] },
    loja_luna: { preco: 169.70, itens: [['LUNA1XMARACUJA', 'AERA40+ Luna - Maracujá'], ['LUNA1XFRUTASVERMELHAS', 'AERA40+ Luna - Frutas Vermelhas']] },
    ads_vita:  { preco: 129.70, itens: [['VITA1X', 'AERA40+ Vita']] },
    loja_vita: { preco: 129.70, itens: [['VITA1X', 'AERA40+ Vita']] }
  };

  function precoMostrado() {
    var el = document.querySelector('[data-price-now]');
    if (!el) return 0;
    var n = parseFloat((el.textContent || '').replace(/[^\d,]/g, '').replace(',', '.'));
    return n > 0 ? n : 0;
  }

  function pushViewContent() {
    var produto = PRODUTOS_DA_PAGINA[CFG.page_id];
    if (!produto) {
      pushEvent('lp_view_content');
      return;
    }
    var preco = precoMostrado() || produto.preco;
    var ecommerce = {
      currency: 'BRL',
      value: preco,
      items: produto.itens.map(function (item) {
        return { item_id: item[0], item_name: item[1], item_variant: item[0], item_brand: 'AERA40+', price: preco, quantity: 1 };
      })
    };
    if (!shouldBlockMarketing()) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null }); // não herdar produto de um push anterior
    }
    pushEvent('lp_view_content', { ecommerce: ecommerce });
    pushEvent('view_item', { ecommerce: ecommerce });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
